import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePlotsStore, type PlotEditorTabId } from '@/store/plots';
import { usePlotEditHistory } from '@/hooks/use-plot-edit-history';
import { 
  Undo, Redo, X, RotateCcw
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { clearDraft, clearActiveSessionMarkers } from '@/lib/drafts/localDraft';
import { idbDeleteDraft, makeDraftKey } from '@/lib/drafts/indexedDbDraft';
import { useParams } from 'wouter';
import { applyJournalTemplate, mmToPx } from '@/lib/applyJournalTemplate';
import { ensureThemeDefaults } from '@/lib/colors/mapping';
import { useCredits } from '@/hooks/useCredits';
import { useQuery } from '@tanstack/react-query';
import { authorizedFetch } from '@/lib/queryClient';
import BillingModal from '@/components/BillingModal';
import { inc as metricsInc } from '@/lib/metrics';
import OutOfCreditsDialog from '@/components/chat/OutOfCreditsDialog';
import { applyBarGrouping, applyOrientation, type BarGrouping, type Orientation } from '@/lib/plotlyBarHelpers';
import { getTraceCapabilities, unionCapabilities } from './capabilities';
import { applyOperation } from './mutation-router';
import { PlotEditorRoot } from './editor/PlotEditorRoot';
import { PlotEditorTabs } from './editor/tabs/PlotEditorTabs';

interface PlotEditorV3Props {
  onClose?: () => void;
  isCompact?: boolean;
}

export default function PlotEditorV3({ onClose }: PlotEditorV3Props) {
  const { projectId: routeProjectId } = useParams<{ projectId?: string }>();
  const { 
    currentPlot, 
    setCurrentPlot,
    editorUI,
    setActiveTab: setActiveTabStore,
    setFocusHint,
    clearSelection,
    resetPlotToBaseline,
    hydration,
  } = usePlotsStore();
  const { 
    initializeHistory,
    saveToHistory,
    // applyFastUpdate,
    undo,
    redo,
    canUndo,
    canRedo,
    resetTo: resetHistoryTo,
  } = usePlotEditHistory();
  
  // Guard to prevent markUserEdited from re-marking dirty during reset
  const isResettingRef = useRef<boolean>(false);
  
  // Check if reset is available (baseline exists and plot is dirty)
  const isDirty = !!(hydration as any)?.guard?.isDirty;
  // Use selector to ensure reactivity - baselineFigureById should persist across resets
  const baselineFigureById = usePlotsStore(s => s.baselineFigureById || {});
  const baselineFigure = currentPlot?.id ? (baselineFigureById[currentPlot.id] || null) : null;
  const canReset = isDirty && !!baselineFigure && !!currentPlot?.id;
  
  // Debug logging to diagnose reset button state
  useEffect(() => {
    if (currentPlot?.id) {
      const hasBaseline = !!baselineFigureById[currentPlot.id];
      console.log('[Reset] canReset check:', {
        plotId: currentPlot.id,
        isDirty,
        hasBaseline,
        canReset,
        baselineKeys: Object.keys(baselineFigureById || {}),
        baselineFigureById: baselineFigureById,
      });
    }
  }, [currentPlot?.id, isDirty, baselineFigure, canReset, baselineFigureById]);
  
  const activeTab = editorUI.activeTab;
  const lastFocusHintTs = useRef<number>(0);
  
  // Consume focusHint to auto-switch tabs (one-shot, ts-based)
  useEffect(() => {
    const hint = editorUI.focusHint;
    if (!hint || !hint.ts) return;
    
    // Only consume if ts changed (avoid loops)
    if (hint.ts === lastFocusHintTs.current) return;
    lastFocusHintTs.current = hint.ts;
    
    // Auto-switch tab if different
    if (hint.tab && hint.tab !== activeTab) {
      setActiveTabStore(hint.tab, 'system');
      // Don't clear hint immediately - let the tab component consume it for section expansion
      // The hint will be cleared after a delay to allow tab to mount and consume
      setTimeout(() => {
        // Only clear if we're now on the target tab (tab switch succeeded)
        const currentState = usePlotsStore.getState();
        if (currentState.editorUI.activeTab === hint.tab) {
          setFocusHint(null);
        }
      }, 200);
    } else if (hint.tab === activeTab) {
      // Already on the correct tab, clear hint after a short delay to allow section expansion
      setTimeout(() => {
        setFocusHint(null);
      }, 200);
    }
  }, [editorUI.focusHint, activeTab, setActiveTabStore, setFocusHint]);
  
  const handleTabChange = (tab: string) => {
    // Type guard: ensure tab is valid PlotEditorTabId
    const validTabs: PlotEditorTabId[] = [
      'data-encodings',
      'axes-scales',
      'colors-styles',
      'labels-annotations',
      'legend-guides',
      'layout-subplots',
    ];
    if (validTabs.includes(tab as PlotEditorTabId)) {
      setActiveTabStore(tab as PlotEditorTabId, 'editor');
    } else {
      console.error(`Invalid tab ID in handleTabChange: "${tab}"`);
    }
  };
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();
  const { data: credits } = useCredits(60_000);
  const [showOutOfCredits, setShowOutOfCredits] = useState<{ open: boolean; resetAt?: string }>({ open: false });
  const [billingOpen, setBillingOpen] = useState(false);
  const { data: config } = useQuery<any>({
    queryKey: ['/api/config'],
    queryFn: async () => {
      const r = await authorizedFetch('/api/config');
      return r.ok ? r.json() : {} as any;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false
  });
  const COST = Math.max(1, Number(config?.credits?.costPerPlot ?? 3) || 3);
  const insufficient = !!(credits?.enabled && (credits?.remaining ?? 0) < COST);
  const gateShownRef = useRef(false);
  useEffect(() => {
    if (insufficient && !gateShownRef.current) {
      gateShownRef.current = true;
      try { metricsInc('credits_gate_shown', { place: 'editor' }); } catch {}
    }
    if (!insufficient) gateShownRef.current = false;
  }, [insufficient]);

  // Get data columns from current plot
  const dataColumns = (currentPlot as any)?.plotSpec?.data?.columns
    ? Object.keys(((currentPlot as any).plotSpec.data.columns))
    : [];

  // Initialize history ONLY when plot changes (not when switching tabs)
  useEffect(() => {
    if (!currentPlot?.plotlyJson) return;
    initializeHistory(currentPlot);
    try {
      let modified = false;
      const updated = JSON.parse(JSON.stringify(currentPlot));
      if (!updated.plotlyJson.layout) updated.plotlyJson.layout = {};
      // Ensure hover is enabled to support reliable point picking
      if (
        updated.plotlyJson.layout.hovermode === false ||
        updated.plotlyJson.layout.hovermode === undefined
      ) {
        updated.plotlyJson.layout.hovermode = 'closest';
        modified = true;
      }
      // Ensure default typography color is black on first load
      if (!updated.plotlyJson.layout.font) updated.plotlyJson.layout.font = {};
      if (!updated.plotlyJson.layout.font.color || updated.plotlyJson.layout.font.color === '') {
        updated.plotlyJson.layout.font.color = '#000000';
        modified = true;
      }
      // Do not suppress hover info; remove prior suppression if present
      if (Array.isArray(updated.plotlyJson.data)) {
        for (const t of updated.plotlyJson.data) {
          if (t.hoverinfo === 'skip') {
            delete t.hoverinfo;
            modified = true;
          }
        }
      }
      if (modified) {
        setCurrentPlot(updated);
      }
    } catch {}
  }, [currentPlot?.id]);

  // When Colors & Styles tab is active and has per-point colors, enable selection mode
  useEffect(() => {
    if (!currentPlot?.plotlyJson) return;
    try {
      let modified = false;
      const updated = JSON.parse(JSON.stringify(currentPlot));
      if (!updated.plotlyJson.layout) updated.plotlyJson.layout = {};
      if (activeTab === 'colors-styles') {
        // Check if point controls are visible (hasPerPointColors)
        const hasPerPointColors = (() => {
          try {
            const data:any[] = Array.isArray((currentPlot as any)?.plotlyJson?.data) ? ((currentPlot as any).plotlyJson.data as any[]) : [];
            const caps = unionCapabilities(data);
            return !!caps.hasPerPointColors;
          } catch { return false; }
        })();
        
        if (hasPerPointColors && updated.plotlyJson.layout.dragmode !== 'select') {
          updated.plotlyJson.layout.dragmode = 'select';
          modified = true;
        }
        if (hasPerPointColors && !updated.plotlyJson.layout.selectdirection) {
          (updated.plotlyJson.layout as any).selectdirection = 'any';
          modified = true;
        }
      }
      if (modified) {
        setCurrentPlot(updated);
      }
    } catch {}
  }, [activeTab, currentPlot?.id]);

  // Universal update handler with proper data flow
  const handleUpdate = useCallback((updateType: string, value: any) => {
    // Read latest state from store to prevent stale state overwrites
    // This ensures sequential updates (e.g., from batched operations) compose correctly
    const latestPlot = usePlotsStore.getState().currentPlot;
    if (!latestPlot?.plotlyJson) return;
    
    setIsProcessing(true);
    
    try {
      console.log('🔄 handleUpdate called:', {
        updateType,
        value
      });
      
      // Deep clone the latest plotly JSON from store state (not closure)
      const updatedJson = JSON.parse(JSON.stringify(latestPlot.plotlyJson));
      
      // Clear annotation defaults to prevent background re-introduction
      if (!updatedJson.layout) updatedJson.layout = {};
      if (updatedJson.layout.annotationdefaults?.bgcolor &&
          updatedJson.layout.annotationdefaults.bgcolor !== 'rgba(0,0,0,0)') {
        console.log('🧹 Clearing annotation default bgcolor');
        updatedJson.layout.annotationdefaults.bgcolor = 'rgba(0,0,0,0)';
      }
      if (updatedJson.layout.annotationdefaults?.bordercolor &&
          updatedJson.layout.annotationdefaults.bordercolor !== 'rgba(0,0,0,0)') {
        console.log('🧹 Clearing annotation default bordercolor');
        updatedJson.layout.annotationdefaults.bordercolor = 'rgba(0,0,0,0)';
      }
      
      // Apply the update
      const success = applyUpdate(updatedJson, updateType, value);
      
      if (success) {
        // Always bump editrevision to trigger Plotly re-render
        if (!updatedJson.layout) updatedJson.layout = {};
        updatedJson.layout.editrevision = (updatedJson.layout.editrevision || 0) + 1;
        
        // Force new data array reference for point updates and textmode/textinfo changes to trigger re-render
        if (updateType.startsWith('point.') || updateType.includes('textmode') || updateType.includes('textinfo')) {
          // Extract trace index from updateType (e.g., 'series.0.textmode' -> 0)
          const traceIndexMatch = updateType.match(/series\.(\d+)\.textmode/);
          const targetTraceIdx = traceIndexMatch ? parseInt(traceIndexMatch[1]) : null;
          
          // Create new array with new trace object references to ensure Plotly detects changes
          // IMPORTANT: The traces have already been modified in applySeriesUpdate, so we need to
          // create new trace objects to ensure Plotly sees the changes
          updatedJson.data = updatedJson.data.map((trace: any, idx: number) => {
            // Create a new trace object with all properties copied
            // This ensures Plotly detects the changes to textmode, text, textposition, etc.
            const newTrace = JSON.parse(JSON.stringify(trace));
            // Log textmode changes for debugging - log the actual trace being updated
            if (updateType.includes('textmode') && (targetTraceIdx === null || idx === targetTraceIdx)) {
              console.log('📊 Final trace state after update:', {
                traceIndex: idx,
                type: newTrace.type,
                textmode: newTrace.textmode,
                mode: newTrace.mode,
                hasText: !!newTrace.text,
                textLength: Array.isArray(newTrace.text) ? newTrace.text.length : 0,
                textposition: newTrace.textposition,
                updateType: updateType,
                hasOriginalText: !!(trace as any)._originalText,
                hasOriginalTextmode: !!(trace as any)._originalTextmode
              });
            }
            return newTrace;
          });
        }
        
        // Update the plot state using setCurrentPlot (like the working version)
        // Use latestPlot to ensure we're updating from the most recent state
        const updatedPlot = {
          ...latestPlot,
          plotlyJson: updatedJson,
          lastEdit: {
            type: updateType,
            value: value,
            timestamp: Date.now()
          }
        };
        
        setCurrentPlot(updatedPlot);
        // Mark plot as dirty so reset button becomes enabled
        try {
          usePlotsStore.getState().markUserEdited?.();
        } catch {}
        saveToHistory(updateType, 'fast');
        
        toast({
          title: "✨ Plot updated",
          description: `Applied ${updateType}`,
          duration: 1000,
        });
      } else {
        throw new Error(`Failed to apply update: ${updateType}`);
      }
    } catch (error) {
      console.error('Update failed:', error);
      toast({
        title: "Update failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  }, [setCurrentPlot, saveToHistory, toast]);

  // Create an updateSpec function for backward compatibility with controls
  // const updateSpec = useCallback((path: string, value: any) => {
  //   handleUpdate(path, value);
  // }, [handleUpdate]);

  // Apply updates to plotly JSON structure
  const applyUpdate = (plotlyJson: any, updateType: string, value: any): boolean => {
    try {
      // Ensure layout exists
      if (!plotlyJson.layout) plotlyJson.layout = {};
      
      // Parse the update type to handle different categories
      const [category, ...path] = updateType.split('.');
      
      // Capability-gated operation router
      if (category === 'op') {
        return tryOperation(plotlyJson, updateType, value);
      }

      switch (category) {
        case 'axis':
          return applyAxisUpdate(plotlyJson, path.join('.'), value);
        
        case 'series':
          return applySeriesUpdate(plotlyJson, path.join('.'), value);
        
        case 'data':
          return applyDataUpdate(plotlyJson, path.join('.'), value);
        
        case 'bar':
          return applyBarUpdate(plotlyJson, path.join('.'), value);
        
        case 'marker':
          return applyMarkerUpdate(plotlyJson, path.join('.'), value);
        
        case 'point':
          return applyPointUpdate(plotlyJson, path.join('.'), value);
        
        case 'clearSelection':
          clearSelection('editor');
          return true;
        
        case 'tooltip':
          return applyTooltipUpdate(plotlyJson, path.join('.'), value);
        
        case 'overlay':
          return applyOverlayUpdate(plotlyJson, path.join('.'), value);
        
        case 'annotation':
          return applyAnnotationUpdate(plotlyJson, path.join('.'), value);
        
        case 'batch':
          return applyBatchUpdate(plotlyJson, path.join('.'), value);

        case 'design':
          return applyDesignUpdate(plotlyJson, path.join('.'), value);
        
        case 'layout':
          return applyLayoutUpdate(plotlyJson, path.join('.'), value);
        
        default:
          // Try to apply as a general layout update
          return applyLayoutUpdate(plotlyJson, updateType, value);
      }
    } catch (error) {
      console.error('Failed to apply update:', error);
      return false;
    }
  };

  // Small mutation router keyed by operation strings, capability guarded
  const tryOperation = (plotlyJson: any, op: string, payload: any): boolean => {
    // For continuousScale, chain editor-specific side-effects after shared mutation
    if (op === 'op.continuousScale:set') {
      const ok = applyOperation(plotlyJson, op, payload);
      if (ok) {
        // Apply full editor sequence
        const scale = String(payload?.scale || 'Viridis');
        applyUpdate(plotlyJson, 'design.normalizeColors', true);
        applyUpdate(plotlyJson, 'design.theme.continuous', scale);
      }
      return ok;
    }
    return applyOperation(plotlyJson, op, payload);
  };

  // (no test exports here; tests exercise the shared router in mutation-router.ts)

  // Axis updates
  const applyAxisUpdate = (plotlyJson: any, path: string, value: any): boolean => {
    const [axis, property] = path.split('.');
    const axisKey = axis === 'x' ? 'xaxis' : axis === 'y' ? 'yaxis' : axis;
    
    if (!plotlyJson.layout[axisKey]) {
      plotlyJson.layout[axisKey] = {};
    }
    
    switch (property) {
      case 'title':
        plotlyJson.layout[axisKey].title = { text: value };
        break;
      case 'type':
        plotlyJson.layout[axisKey].type = value; // 'linear', 'log', 'date', 'category'
        break;
      case 'range':
        plotlyJson.layout[axisKey].range = value;
        plotlyJson.layout[axisKey].autorange = false;
        break;
      case 'autorange':
        plotlyJson.layout[axisKey].autorange = value;
        break;
      case 'showgrid':
        plotlyJson.layout[axisKey].showgrid = value;
        break;
      case 'gridcolor':
        plotlyJson.layout[axisKey].gridcolor = value;
        break;
      case 'showline':
        plotlyJson.layout[axisKey].showline = value;
        break;
      case 'zeroline':
        plotlyJson.layout[axisKey].zeroline = value;
        break;
      case 'tickformat':
        plotlyJson.layout[axisKey].tickformat = value;
        break;
      case 'nticks':
        plotlyJson.layout[axisKey].nticks = value;
        break;
      default:
        plotlyJson.layout[axisKey][property] = value;
    }
    
    return true;
  };

  // Series/line updates
  const applySeriesUpdate = (plotlyJson: any, path: string, value: any): boolean => {
    if (!plotlyJson.data || plotlyJson.data.length === 0) return false;
    
    const [index, ...properties] = path.split('.');
    const traceIndex = index === 'all' ? -1 : parseInt(index);
    const property = properties.join('.');
    
    // Get the actual trace objects from plotlyJson.data (not copies)
    const traceIndices = traceIndex === -1 
      ? plotlyJson.data.map((_: any, i: number) => i)
      : [traceIndex];
    
    for (const traceIdx of traceIndices) {
      const trace = plotlyJson.data[traceIdx];
      if (!trace) continue;
      
      // Pie/funnelarea slice color control: series.<traceIndex>.sliceColor.<i>
      const sliceColorMatch = property.match(/^sliceColor\.(\d+)$/);
      if (sliceColorMatch && (trace.type === 'pie' || trace.type === 'funnelarea')) {
        const sliceIdx = parseInt(sliceColorMatch[1]);
        if (!trace.marker) trace.marker = {};
        const labels:any[] = (trace.labels || trace.label || []) as any[];
        const len = Array.isArray(labels) ? labels.length : (Array.isArray(trace.values) ? trace.values.length : 0);
        const ensure = (arr:any, def:string) => {
          const base = Array.isArray(arr) ? arr.slice() : new Array(len).fill(def);
          while (base.length < len) base.push(def);
          return base;
        };
        const colors = ensure(trace.marker.colors, value || '#1f77b4');
        if (sliceIdx >= 0 && sliceIdx < len) colors[sliceIdx] = value;
        trace.marker.colors = colors;
        return true;
      }

      // Sankey node color control: series.<traceIndex>.sankeyNodeColor.<i>
      const sankeyNodeColorMatch = property.match(/^sankeyNodeColor\.(\d+)$/);
      if (sankeyNodeColorMatch && trace.type === 'sankey') {
        const nodeIdx = parseInt(sankeyNodeColorMatch[1]);
        if (!trace.node) trace.node = {};
        const labels:any[] = (trace.node.labels || trace.node.label || []) as any[];
        const len = Array.isArray(labels) ? labels.length : 0;
        const ensure = (arr:any, def:string) => {
          const base = Array.isArray(arr) ? arr.slice() : new Array(len).fill(def);
          while (base.length < len) base.push(def);
          return base;
        };
        const colors = ensure(trace.node.color, value || '#1f77b4');
        if (nodeIdx >= 0 && nodeIdx < len) colors[nodeIdx] = value;
        trace.node.color = colors;
        return true;
      }

      switch (property) {
        case 'line.decoupled': {
          // marker/line decoupling is a UI hint; if true, keep line color independent
          (trace as any)._lumiTheme = { ...(trace as any)._lumiTheme, decoupledLine: !!value };
          return true;
        }
        case 'color': {
          // For bars - set the marker color
          if (trace.type === 'bar') {
            if (!trace.marker) trace.marker = {};
            trace.marker.color = value;
          }
          // For histograms - set marker color
          else if (trace.type === 'histogram') {
            if (!trace.marker) trace.marker = {};
            trace.marker.color = value;
          }
          // For scatter/line plots - set both marker and line color
          else if (trace.type === 'scatter' || !trace.type) {
            if (!trace.marker) trace.marker = {};
            trace.marker.color = value;
            // Also set line color to match
            if (!trace.line) trace.line = {};
            trace.line.color = value;
          }
          // For box/violin - fillcolor controls interior; marker controls points
          else if (trace.type === 'box' || trace.type === 'violin') {
            trace.fillcolor = value;
            if (trace.marker) {
              if (!trace.marker) trace.marker = {};
              trace.marker.color = trace.marker.color ?? value;
            }
          }
          // For funnel and waterfall - use marker color
          else if (trace.type === 'funnel' || trace.type === 'waterfall') {
            if (!trace.marker) trace.marker = {};
            trace.marker.color = value;
          }
          // For pie/funnelarea - slice colors are handled via router; avoid generic no-op
          else if (trace.type === 'pie' || trace.type === 'funnelarea') {
            // do nothing here; slice colors controlled by op.sliceColor:set
          }
          // For area fills: only apply if line-capable trace per capabilities
          try {
            const caps = getTraceCapabilities(trace as any);
            if (trace.fill && caps.supportsPerTraceFill) {
              trace.fillcolor = value + '40';
            }
          } catch {}
          break;
        }
        case 'line.color':
          // Line color - controls the outline/border
          if (!trace.line) trace.line = {};
          trace.line.color = value;
          // For bars, also set the marker line
          if (trace.type === 'bar') {
            if (!trace.marker) trace.marker = {};
            if (!trace.marker.line) trace.marker.line = {};
            trace.marker.line.color = value;
            trace.marker.line.width = 1; // Ensure outline is visible
          }
          // For scatter plots with markers
          if (trace.type === 'scatter' || !trace.type) {
            if (!trace.marker) trace.marker = {};
            if (!trace.marker.line) trace.marker.line = {};
            trace.marker.line.color = value;
            trace.marker.line.width = 1;
          }
          // For pie/funnelarea/funnel traces, outline is marker.line
          if (trace.type === 'pie' || trace.type === 'funnelarea' || trace.type === 'funnel') {
            if (!trace.marker) trace.marker = {};
            if (!trace.marker.line) trace.marker.line = {};
            trace.marker.line.color = value;
            if (typeof trace.marker.line.width !== 'number' || trace.marker.line.width === 0) {
              trace.marker.line.width = 1;
            }
          }
          // Waterfall: outline color for all segments
          if (trace.type === 'waterfall') {
            if (!trace.increasing) trace.increasing = {} as any;
            if (!trace.decreasing) trace.decreasing = {} as any;
            if (!trace.totals) trace.totals = {} as any;
            const inc = (trace.increasing as any).marker = (trace.increasing as any).marker || {};
            const dec = (trace.decreasing as any).marker = (trace.decreasing as any).marker || {};
            const tot = (trace.totals as any).marker = (trace.totals as any).marker || {};
            inc.line = inc.line || {}; dec.line = dec.line || {}; tot.line = tot.line || {};
            inc.line.color = value; dec.line.color = value; tot.line.color = value;
            if (typeof inc.line.width !== 'number' || inc.line.width === 0) inc.line.width = 1;
            if (typeof dec.line.width !== 'number' || dec.line.width === 0) dec.line.width = 1;
            if (typeof tot.line.width !== 'number' || tot.line.width === 0) tot.line.width = 1;
          }
          break;
        case 'line.width':
          if (!trace.line) trace.line = {};
          trace.line.width = value;
          // Apply outline width consistently across trace types
          if (trace.type === 'bar' || trace.type === 'histogram' || trace.type === 'waterfall' || trace.type === 'funnel') {
            if (!trace.marker) trace.marker = {};
            if (!trace.marker.line) trace.marker.line = {};
            trace.marker.line.width = value;
            if (trace.marker.line.color === undefined) trace.marker.line.color = '#333333';
          }
          if (trace.type === 'scatter' || !trace.type) {
            if (!trace.marker) trace.marker = {};
            if (!trace.marker.line) trace.marker.line = {};
            trace.marker.line.width = value;
            if (trace.marker.line.color === undefined) trace.marker.line.color = '#333333';
          }
          // For pie/funnelarea outlines
          if (trace.type === 'pie' || trace.type === 'funnelarea') {
            if (!trace.marker) trace.marker = {};
            if (!trace.marker.line) trace.marker.line = {};
            trace.marker.line.width = value;
            if (trace.marker.line.color === undefined) trace.marker.line.color = '#333333';
          }
          // Waterfall: outline width for all segments
          if (trace.type === 'waterfall') {
            if (!trace.increasing) trace.increasing = {} as any;
            if (!trace.decreasing) trace.decreasing = {} as any;
            if (!trace.totals) trace.totals = {} as any;
            const inc = (trace.increasing as any).marker = (trace.increasing as any).marker || {};
            const dec = (trace.decreasing as any).marker = (trace.decreasing as any).marker || {};
            const tot = (trace.totals as any).marker = (trace.totals as any).marker || {};
            inc.line = inc.line || {}; dec.line = dec.line || {}; tot.line = tot.line || {};
            inc.line.width = value; dec.line.width = value; tot.line.width = value;
            if (inc.line.color === undefined) inc.line.color = '#333333';
            if (dec.line.color === undefined) dec.line.color = '#333333';
            if (tot.line.color === undefined) tot.line.color = '#333333';
          }
          if (trace.type === 'box' || trace.type === 'violin') {
            if (!trace.line) trace.line = {};
            trace.line.width = value;
          }
          // Ensure visible when width > 0 for line-capable traces
          if (value > 0 && trace.mode && !trace.mode.includes('lines')) {
            trace.mode = trace.mode ? trace.mode + '+lines' : 'lines';
          }
          break;
        case 'itemSize':
          // Universal size for bars, markers, boxes, etc.
          if (trace.type === 'bar') {
            // For bars, width is a value between 0 and 1 (fraction of available space)
            trace.width = Math.min(1, Math.max(0.1, value / 20)); // Scale 2-20 to 0.1-1
          } else if (trace.type === 'box' || trace.type === 'violin') {
            trace.width = Math.min(1, Math.max(0.1, value / 20));
          } else {
            // For scatter/line plots
            if (!trace.marker) trace.marker = {};
            trace.marker.size = value;
          }
          break;
        case 'gap':
          // Applied at layout level for all plots
          if (!plotlyJson.layout) plotlyJson.layout = {};
          plotlyJson.layout.bargap = value;
          plotlyJson.layout.boxgap = value;
          plotlyJson.layout.violingap = value;
          // Also set bargroupgap for grouped bars
          plotlyJson.layout.bargroupgap = value;
          // Waterfall-specific gaps (Plotly supports waterfallgap and waterfallgroupgap)
          (plotlyJson.layout as any).waterfallgap = value;
          (plotlyJson.layout as any).waterfallgroupgap = value;
          break;
        case 'name':
          trace.name = value;
          // For pie/funnelarea: avoid double legend titles. Use legendgrouptitle only when multiple such traces; otherwise use global legend title.
          if (trace.type === 'pie' || trace.type === 'funnelarea') {
            try {
              const all = Array.isArray(plotlyJson.data) ? (plotlyJson.data as any[]) : [];
              const pieLike = all.filter((t:any) => {
                const tt = String(t?.type || '').toLowerCase();
                return tt === 'pie' || tt === 'funnelarea';
              });
              const pieCount = pieLike.length;
              // Ensure layout containers exist
              plotlyJson.layout = plotlyJson.layout || {};
              plotlyJson.layout.legend = plotlyJson.layout.legend || {};

              if (pieCount > 1) {
                // Set group title on this trace and clear global legend title to avoid duplication
                trace.legendgroup = trace.legendgroup || `trace_${traceIndex}`;
                (trace as any).legendgrouptitle = { text: String(value || '') } as any;
                if ((plotlyJson.layout.legend as any).title?.text) {
                  (plotlyJson.layout.legend as any).title = { text: '' } as any;
                }
              } else {
                // Single pie-like trace: set global legend title and remove group title
                if ((trace as any).legendgrouptitle) delete (trace as any).legendgrouptitle;
                if (trace.legendgroup) delete (trace as any).legendgroup;
                (plotlyJson.layout.legend as any).title = { ...(plotlyJson.layout.legend as any).title, text: String(value || '') } as any;
              }
            } catch {}
          }
          break;
        case 'waterfall.increasing': {
          if (trace.type === 'waterfall') {
            if (!trace.increasing) trace.increasing = {} as any;
            const mk = (trace.increasing as any).marker = (trace.increasing as any).marker || {};
            mk.color = value;
            return true;
          }
          break;
        }
        case 'waterfall.decreasing': {
          if (trace.type === 'waterfall') {
            if (!trace.decreasing) trace.decreasing = {} as any;
            const mk = (trace.decreasing as any).marker = (trace.decreasing as any).marker || {};
            mk.color = value;
            return true;
          }
          break;
        }
        case 'waterfall.totals': {
          if (trace.type === 'waterfall') {
            if (!trace.totals) trace.totals = {} as any;
            const mk = (trace.totals as any).marker = (trace.totals as any).marker || {};
            mk.color = value;
            return true;
          }
          break;
        }
        case 'candlestick.increasing': {
          if (trace.type === 'candlestick' || trace.type === 'ohlc') {
            if (!trace.increasing) trace.increasing = {} as any;
            const inc = trace.increasing as any;
            if (!inc.line) inc.line = {};
            inc.line.color = value;
            inc.fillcolor = value;
            return true;
          }
          break;
        }
        case 'candlestick.decreasing': {
          if (trace.type === 'candlestick' || trace.type === 'ohlc') {
            if (!trace.decreasing) trace.decreasing = {} as any;
            const dec = trace.decreasing as any;
            if (!dec.line) dec.line = {};
            dec.line.color = value;
            dec.fillcolor = value;
            return true;
          }
          break;
        }
        case 'showlegend':
          trace.showlegend = value;
          break;
        case 'yaxis':
          // Assign trace to Y axis (y, y2, y3, etc.)
          trace.yaxis = value;
          break;
        case 'xaxis':
          // Assign trace to X axis (x, x2, x3, etc.)
          trace.xaxis = value;
          break;
        case 'xbins.size':
          // Histogram bin size
          if (!trace.xbins) trace.xbins = {};
          trace.xbins.size = value;
          break;
        case 'xbins.start':
          // Histogram bin start
          if (!trace.xbins) trace.xbins = {};
          trace.xbins.start = value;
          break;
        case 'xbins.end':
          // Histogram bin end
          if (!trace.xbins) trace.xbins = {};
          trace.xbins.end = value;
          break;
        case 'histnorm':
          // Histogram normalization mode
          trace.histnorm = value || undefined;
          break;
        case 'marker.symbol':
          // Set marker symbol shape
          if (!trace.marker) trace.marker = {};
          trace.marker.symbol = value;
          // Open/unfilled symbols require marker.line.width > 0 to be visible
          const openSymbols = ['cross', 'x', 'triangle-up', 'triangle-down', 'y-up', 'hexagon', 'star'];
          if (openSymbols.includes(value)) {
            if (!trace.marker.line) trace.marker.line = {};
            // Only set line width if it's 0 or undefined (don't override user's explicit setting)
            if (!trace.marker.line.width || trace.marker.line.width === 0) {
              trace.marker.line.width = 1;
            }
          }
          break;
        case 'showMarkers':
          // Toggle markers on/off
          if (value) {
            if (trace.mode === 'lines' || !trace.mode) {
              trace.mode = 'lines+markers';
            } else if (trace.mode === 'none') {
              trace.mode = 'markers';
            }
            // Ensure marker exists
            if (!trace.marker) trace.marker = {};
          } else {
            if (trace.mode === 'lines+markers') {
              trace.mode = 'lines';
            } else if (trace.mode === 'markers') {
              trace.mode = 'none';
            }
          }
          break;
        case 'textmode':
          // Control data label display mode
          // For bar charts: 'none' | 'label' | 'label+percent' | 'value' | 'value+percent' | 'percent'
          // For scatter/line: add 'text' to mode or use textposition
          // For pie/funnelarea/treemap/sunburst/icicle: use textinfo instead
          // Special value 'restore' will restore the original textmode/mode/textinfo if it was stored
          const traceType = String(trace.type || '').toLowerCase();
          
          // Handle pie-like charts (use textinfo instead of textmode)
          if (traceType === 'pie' || traceType === 'funnelarea' || traceType === 'treemap' || 
              traceType === 'sunburst' || traceType === 'icicle') {
            if (value === 'none' || value === false) {
              // Store original textinfo before disabling
              const originalTextinfo = trace.textinfo;
              if (trace.textinfo && trace.textinfo !== 'none' && !(trace as any)._originalTextinfo) {
                (trace as any)._originalTextinfo = trace.textinfo;
                console.log('📝 Stored original textinfo:', trace.textinfo, 'for trace', traceIdx);
              }
              // Disable labels by setting textinfo to 'none'
              trace.textinfo = 'none';
              console.log('🔴 Disabled labels - set textinfo to none. Original was:', originalTextinfo, 'Trace:', traceIdx);
            } else if (value === 'restore') {
              // Restore original textinfo if it exists
              const originalTextinfo = (trace as any)._originalTextinfo;
              if (originalTextinfo && originalTextinfo !== 'none') {
                trace.textinfo = originalTextinfo;
                delete (trace as any)._originalTextinfo;
                console.log('🟢 Restored original textinfo:', originalTextinfo, 'for trace', traceIdx);
              } else if (trace.textinfo && trace.textinfo !== 'none') {
                // Already has textinfo set - preserve it
                console.log('🟢 Preserving existing textinfo:', trace.textinfo, 'for trace', traceIdx);
              } else {
                // No original - use default 'label+percent'
                trace.textinfo = 'label+percent';
                console.log('🟢 Enabled labels with default textinfo: label+percent for trace', traceIdx);
              }
            } else {
              // Normal enable with specific value
              if ((trace as any)._originalTextinfo && (trace as any)._originalTextinfo !== 'none') {
                trace.textinfo = (trace as any)._originalTextinfo;
                delete (trace as any)._originalTextinfo;
              } else if (trace.textinfo && trace.textinfo !== 'none') {
                // Already enabled - preserve it
              } else {
                trace.textinfo = value || 'label+percent';
              }
            }
          } else if (trace.type === 'bar' || trace.type === 'histogram') {
            if (value === 'none' || value === false) {
              // Store original configuration before disabling
              const originalTextmode = trace.textmode;
              const originalText = trace.text;
              const originalTextposition = trace.textposition;
              const originalTexttemplate = trace.texttemplate;
              
              // Store original textmode if it exists and is not 'none'
              if (trace.textmode && trace.textmode !== 'none' && !(trace as any)._originalTextmode) {
                (trace as any)._originalTextmode = trace.textmode;
                console.log('📝 Stored original textmode:', trace.textmode, 'for trace', traceIdx);
              }
              
              // Store original text array if it exists (even if textmode wasn't set)
              if (trace.text && Array.isArray(trace.text) && trace.text.length > 0 && !(trace as any)._originalText) {
                (trace as any)._originalText = trace.text;
                console.log('📝 Stored original text array for trace', traceIdx);
              }
              
              // Store original textposition if it exists
              if (trace.textposition && !(trace as any)._originalTextposition) {
                (trace as any)._originalTextposition = trace.textposition;
              }
              
              // Store original texttemplate if it exists
              if (trace.texttemplate && !(trace as any)._originalTexttemplate) {
                (trace as any)._originalTexttemplate = trace.texttemplate;
              }
              
              // Disable labels: set textmode to 'none' and clear text/textposition/texttemplate
              trace.textmode = 'none';
              if (trace.text) delete trace.text;
              if (trace.textposition) delete trace.textposition;
              if (trace.texttemplate) delete trace.texttemplate;
              
              console.log('🔴 Disabled labels - set textmode to none. Original was:', originalTextmode || 'text array', 'Trace:', traceIdx);
            } else if (value === 'restore') {
              // When enabling with 'restore', always restore original textmode if it exists
              const originalTextmode = (trace as any)._originalTextmode;
              const currentTextmode = trace.textmode;
              if (originalTextmode && originalTextmode !== 'none') {
                // Restore original textmode
                trace.textmode = originalTextmode;
                delete (trace as any)._originalTextmode;
                
                // Restore textposition if it was stored
                if ((trace as any)._originalTextposition) {
                  trace.textposition = (trace as any)._originalTextposition;
                  delete (trace as any)._originalTextposition;
                }
                
                // Restore text array if it was stored
                if ((trace as any)._originalText && Array.isArray((trace as any)._originalText)) {
                  trace.text = (trace as any)._originalText;
                  delete (trace as any)._originalText;
                }
                
                // Restore texttemplate if it was stored
                if ((trace as any)._originalTexttemplate) {
                  trace.texttemplate = (trace as any)._originalTexttemplate;
                  delete (trace as any)._originalTexttemplate;
                }
                
                console.log('🟢 Restored original textmode:', originalTextmode, 'for trace', traceIdx);
              } else if ((trace as any)._originalText && Array.isArray((trace as any)._originalText)) {
                // No textmode but had text array - restore text array and set textmode to 'value'
                trace.text = (trace as any)._originalText;
                delete (trace as any)._originalText;
                trace.textmode = 'value';
                
                // Restore textposition if it was stored
                if ((trace as any)._originalTextposition) {
                  trace.textposition = (trace as any)._originalTextposition;
                  delete (trace as any)._originalTextposition;
                }
                
                console.log('🟢 Restored original text array with textmode: value for trace', traceIdx);
              } else if (trace.textmode && trace.textmode !== 'none') {
                // Already has a textmode set - preserve it, don't overwrite
                console.log('🟢 Preserving existing textmode:', trace.textmode, 'for trace', traceIdx);
              } else {
                // No original and no current - use 'value' as default
                trace.textmode = 'value';
                console.log('🟢 Enabled labels with default textmode: value for trace', traceIdx);
              }
              // Don't populate text array - preserve existing text array if it exists
              // Plotly will use existing text array or y values automatically based on textmode
            } else {
              // Normal enable with specific value - check if we should restore or use provided value
              if ((trace as any)._originalTextmode && (trace as any)._originalTextmode !== 'none') {
                // Restore original instead of using provided value
                trace.textmode = (trace as any)._originalTextmode;
                delete (trace as any)._originalTextmode;
              } else if (trace.textmode && trace.textmode !== 'none') {
                // Already enabled - preserve it
              } else {
                // Use provided value
                trace.textmode = value;
              }
            }
          } else if (trace.type === 'scatter' || trace.type === 'scattergl' || !trace.type) {
            // For scatter plots, add/remove 'text' from mode
            if (value === 'none' || value === false) {
              // Store original mode before disabling (if not already stored)
              const currentMode = trace.mode || '';
              if (currentMode && typeof currentMode === 'string' && currentMode.includes('text') && !(trace as any)._originalMode) {
                (trace as any)._originalMode = currentMode;
                console.log('📝 Stored original mode:', currentMode, 'for trace', traceIdx);
              }
              // Store original text array and textposition if they exist
              if (trace.text && Array.isArray(trace.text) && trace.text.length > 0 && !(trace as any)._originalText) {
                (trace as any)._originalText = trace.text;
              }
              if (trace.textposition && !(trace as any)._originalTextposition) {
                (trace as any)._originalTextposition = trace.textposition;
              }
              
              // Remove 'text' from mode properly
              const beforeMode = trace.mode;
              if (trace.mode && typeof trace.mode === 'string') {
                const modes = trace.mode.split('+').filter((m: string) => m.trim() !== 'text');
                if (modes.length === 0) {
                  trace.mode = 'markers'; // Default fallback
                } else {
                  trace.mode = modes.join('+');
                }
              }
              
              // Clear text array and textposition when disabling
              if (trace.text) delete trace.text;
              if (trace.textposition) delete trace.textposition;
              
              console.log('🔴 Disabled labels - removed text from mode. Before:', beforeMode, 'After:', trace.mode, 'Trace:', traceIdx);
            } else if (value === 'restore') {
              // When enabling with 'restore', always restore original mode if it exists
              const originalMode = (trace as any)._originalMode;
              const currentMode = trace.mode;
              if (originalMode) {
                // Restore original mode
                trace.mode = originalMode;
                delete (trace as any)._originalMode;
                
                // Restore text array if it was stored
                if ((trace as any)._originalText && Array.isArray((trace as any)._originalText)) {
                  trace.text = (trace as any)._originalText;
                  delete (trace as any)._originalText;
                }
                
                // Restore textposition if it was stored
                if ((trace as any)._originalTextposition) {
                  trace.textposition = (trace as any)._originalTextposition;
                  delete (trace as any)._originalTextposition;
                }
                
                console.log('🟢 Restored original mode:', originalMode, 'for trace', traceIdx);
              } else if ((trace as any)._originalText && Array.isArray((trace as any)._originalText)) {
                // No original mode but had text array - restore text array and add 'text' to mode
                trace.text = (trace as any)._originalText;
                delete (trace as any)._originalText;
                
                const beforeMode = trace.mode;
                if (!trace.mode || typeof trace.mode !== 'string') {
                  trace.mode = 'markers+text';
                } else if (!trace.mode.includes('text')) {
                  trace.mode = trace.mode + '+text';
                }
                
                // Restore textposition if it was stored
                if ((trace as any)._originalTextposition) {
                  trace.textposition = (trace as any)._originalTextposition;
                  delete (trace as any)._originalTextposition;
                }
                
                console.log('🟢 Restored original text array and added text to mode. Before:', beforeMode, 'After:', trace.mode, 'Trace:', traceIdx);
              } else if (trace.mode && typeof trace.mode === 'string' && trace.mode.includes('text')) {
                // Already has 'text' in mode - preserve it, don't change
                console.log('🟢 Preserving existing mode with text:', trace.mode, 'for trace', traceIdx);
              } else {
                // No original - add 'text' to mode
                const beforeMode = trace.mode;
                if (!trace.mode || typeof trace.mode !== 'string') {
                  trace.mode = 'markers+text';
                } else if (!trace.mode.includes('text')) {
                  trace.mode = trace.mode + '+text';
                }
                console.log('🟢 Enabled labels - added text to mode. Before:', beforeMode, 'After:', trace.mode, 'Trace:', traceIdx);
              }
            } else {
              // Normal enable with specific value - check if we should restore or add text
              if ((trace as any)._originalMode) {
                // Restore original instead of using provided value
                trace.mode = (trace as any)._originalMode;
                delete (trace as any)._originalMode;
              } else if (trace.mode && typeof trace.mode === 'string' && trace.mode.includes('text')) {
                // Already enabled - preserve it
              } else {
                // Add 'text' to mode
                if (!trace.mode || typeof trace.mode !== 'string') {
                  trace.mode = 'markers+text';
                } else if (!trace.mode.includes('text')) {
                  trace.mode = trace.mode + '+text';
                }
              }
            }
          }
          break;
        case 'textposition':
          // Control data label position
          // Options: 'top' | 'middle' | 'bottom' | 'left' | 'right' | 'top left' | etc.
          // Note: textposition works for both bar/histogram (with textmode) and scatter plots (with mode)
          trace.textposition = value;
          // Ensure text mode is enabled if position is set
          if (trace.type === 'bar' || trace.type === 'histogram') {
            // Bar charts use textmode, ensure it's set
            if (!trace.textmode || trace.textmode === 'none') {
              trace.textmode = 'value';
            }
            // Bar charts also support textposition, ensure data exists
            const dataArray = Array.isArray(trace.y) ? trace.y : (Array.isArray(trace.x) ? trace.x : null);
            if (dataArray && dataArray.length > 0 && !trace.text) {
              trace.text = dataArray.map((v: any) => String(v));
            }
          } else if (trace.type === 'scatter' || trace.type === 'scattergl' || !trace.type) {
            // Scatter plots use mode with 'text', ensure it's included
            if (!trace.mode || typeof trace.mode !== 'string') {
              trace.mode = 'markers+text';
            } else if (!trace.mode.includes('text')) {
              trace.mode = trace.mode + '+text';
            }
            // Ensure text array exists
            if (Array.isArray(trace.y) && trace.y.length > 0 && !trace.text) {
              trace.text = trace.y.map((v: any) => String(v));
            }
          }
          break;
        default:
          setNestedProperty(trace, property, value);
      }
    }
    
    return true;
  };

  // Bar updates (simplified)
  const applyBarUpdate = (plotlyJson: any, path: string, value: any): boolean => {
    if (!plotlyJson.data || plotlyJson.data.length === 0) return false;
    
    const barTraces = plotlyJson.data.filter((t: any) => t.type === 'bar');
    if (barTraces.length === 0) return false;
    
    // Ensure layout exists
    if (!plotlyJson.layout) plotlyJson.layout = {};
    
    switch (path) {
      case 'grouping':
        // Apply bar grouping using helper function
        applyBarGrouping(plotlyJson.layout, value as BarGrouping);
        
        // Remove offsetgroup/alignmentgroup when switching to stacked modes
        // These properties prevent proper stacking
        if (value === 'stacked' || value === 'percent100') {
          for (const trace of barTraces) {
            delete (trace as any).offsetgroup;
            delete (trace as any).alignmentgroup;
          }
        }
        return true;
      
      case 'orientation':
        // Apply orientation using helper function (mutates data and layout)
        applyOrientation(plotlyJson.data, plotlyJson.layout, value as Orientation);
        return true;
      
      case 'barSeparators':
        // Add visual separators between bars
        for (const trace of barTraces) {
          if (!trace.marker) trace.marker = {};
          if (!trace.marker.line) trace.marker.line = {};
          trace.marker.line.width = value ? 1 : 0;
          trace.marker.line.color = 'rgba(0,0,0,0.2)';
        }
        return true;
      
      default:
        // Fallback to per-trace property updates
        for (const trace of barTraces) {
          setNestedProperty(trace, path, value);
        }
        return true;
    }
  };

  // Marker/point updates
  const applyMarkerUpdate = (plotlyJson: any, path: string, value: any): boolean => {
    if (!plotlyJson.data || plotlyJson.data.length === 0) return false;
    
    for (const trace of plotlyJson.data) {
      if (!trace.marker) trace.marker = {};
      
      switch (path) {
        case 'size':
          trace.marker.size = value;
          break;
        case 'color':
          trace.marker.color = value;
          break;
        case 'symbol':
          trace.marker.symbol = value; // 'circle', 'square', 'diamond', 'cross', etc.
          break;
        case 'opacity':
          trace.marker.opacity = value;
          break;
        case 'line.color':
          if (!trace.marker.line) trace.marker.line = {};
          trace.marker.line.color = value;
          break;
        case 'line.width':
          if (!trace.marker.line) trace.marker.line = {};
          trace.marker.line.width = value;
          break;
        default:
          setNestedProperty(trace.marker, path, value);
      }
    }
    
    return true;
  };

  // Individual point updates
  const applyPointUpdate = (plotlyJson: any, path: string, value: any): boolean => {
    const [traceIndex, pointIndex, ...properties] = path.split('.');
    const property = properties.join('.');
    
    console.log('🎯 applyPointUpdate called:', { path, value, traceIndex, pointIndex, property });
    
    const trace = plotlyJson.data?.[parseInt(traceIndex)];
    if (!trace) {
      console.error('❌ Trace not found:', traceIndex);
      return false;
    }
    
    const pointIdx = parseInt(pointIndex);
    const dataLength = trace.x?.length || trace.y?.length || 0;
    if (pointIdx >= dataLength) {
      console.error('❌ Point index out of range:', pointIdx, 'dataLength:', dataLength);
      return false;
    }
    
    // Ensure marker structure exists
    if (!trace.marker) trace.marker = {};
    
    // Helper to convert scalar to array if needed
    const ensureArray = (prop: any, defaultValue: any) => {
      if (!Array.isArray(prop)) {
        // Convert scalar to array filled with the same value
        const fillValue = prop !== undefined && prop !== null ? prop : defaultValue;
        const arr = new Array(dataLength).fill(fillValue);
        return arr;
      }
      // Ensure array is long enough
      while (prop.length < dataLength) {
        const lastValue = prop.length > 0 ? prop[prop.length - 1] : defaultValue;
        prop.push(lastValue);
      }
      return prop;
    };
    
    switch (property) {
      case 'marker.color':
        console.log('🎨 Before color update:', {
          currentColor: trace.marker.color,
          isArray: Array.isArray(trace.marker.color),
          pointIdx,
          value
        });
        const colorArray = ensureArray(trace.marker.color, '#1f77b4');
        colorArray[pointIdx] = value;
        // Create a new array reference to force Plotly to detect the change
        trace.marker.color = [...colorArray];
        console.log('✅ After color update:', { 
          pointIdx, 
          value, 
          markerColors: trace.marker.color,
          actualColor: trace.marker.color[pointIdx]
        });
        break;
        
      case 'marker.opacity':
        const opacityArray = ensureArray(trace.marker.opacity, 1);
        opacityArray[pointIdx] = value;
        // Create a new array reference to force Plotly to detect the change
        trace.marker.opacity = [...opacityArray];
        break;
        
      case 'marker.size':
        const sizeArray = ensureArray(trace.marker.size, 8);
        sizeArray[pointIdx] = value;
        // Create a new array reference to force Plotly to detect the change
        trace.marker.size = [...sizeArray];
        break;
        
      case 'marker.line.color':
        if (!trace.marker.line) trace.marker.line = {};
        const lineColorArray = ensureArray(trace.marker.line.color, '#333333');
        lineColorArray[pointIdx] = value;
        // Create a new array reference to force Plotly to detect the change
        trace.marker.line.color = [...lineColorArray];
        break;
        
      case 'marker.line.width':
        if (!trace.marker.line) trace.marker.line = {};
        const lineWidthArray = ensureArray(trace.marker.line.width, 1);
        lineWidthArray[pointIdx] = value;
        // Create a new array reference to force Plotly to detect the change
        trace.marker.line.width = [...lineWidthArray];
        break;
        
      default:
        console.error('❌ Unknown point property:', property);
        return false;
    }
    
    console.log('✅ Point update successful');
    return true;
  };

  // Tooltip updates
  const applyTooltipUpdate = (plotlyJson: any, path: string, value: any): boolean => {
    switch (path) {
      case 'mode':
        plotlyJson.layout.hovermode = value; // 'x', 'y', 'closest', false
        break;
      case 'bgcolor':
        if (!plotlyJson.layout.hoverlabel) plotlyJson.layout.hoverlabel = {};
        plotlyJson.layout.hoverlabel.bgcolor = value;
        break;
      case 'bordercolor':
        if (!plotlyJson.layout.hoverlabel) plotlyJson.layout.hoverlabel = {};
        plotlyJson.layout.hoverlabel.bordercolor = value;
        break;
      case 'font.size':
        if (!plotlyJson.layout.hoverlabel) plotlyJson.layout.hoverlabel = {};
        if (!plotlyJson.layout.hoverlabel.font) plotlyJson.layout.hoverlabel.font = {};
        plotlyJson.layout.hoverlabel.font.size = value;
        break;
      case 'font.color':
        if (!plotlyJson.layout.hoverlabel) plotlyJson.layout.hoverlabel = {};
        if (!plotlyJson.layout.hoverlabel.font) plotlyJson.layout.hoverlabel.font = {};
        plotlyJson.layout.hoverlabel.font.color = value;
        break;
      default:
        // Handle custom hovertemplate for traces
        if (plotlyJson.data) {
          for (const trace of plotlyJson.data) {
            trace.hovertemplate = value;
          }
        }
    }
    
    return true;
  };

  // Batch updates allow multiple mutations to apply against the same snapshot
  // Data & Encodings updates
  const applyDataUpdate = (plotlyJson: any, path: string, value: any): boolean => {
    try {
      const [operation] = path.split('.');
      
      switch (operation) {
        case 'reorder':
          // Reorder traces array
          if (Array.isArray(value)) {
            plotlyJson.data = value;
            return true;
          }
          return false;
        
        case 'orientation':
          // Apply orientation changes (handled via batch in component)
          if (value && value.traces) {
            plotlyJson.data = value.traces;
            return true;
          }
          return false;
        
        case 'filter':
          // Apply category filters
          if (value && value.traces) {
            plotlyJson.data = value.traces;
            return true;
          }
          return false;
        
        case 'filterNumeric':
          // Apply numeric filters
          if (value && value.traces) {
            plotlyJson.data = value.traces;
            return true;
          }
          return false;
        
        case 'reset':
          // Reset to original data
          if (Array.isArray(value)) {
            plotlyJson.data = value;
            return true;
          }
          return false;
        
        case 'volcano':
          // Apply volcano plot updates
          if (value && value.traces && value.layout) {
            plotlyJson.data = value.traces;
            plotlyJson.layout = { ...plotlyJson.layout, ...value.layout };
            return true;
          }
          return false;
        
        case 'hexbin':
          // Apply hexbin updates
          if (value && value.traces) {
            plotlyJson.data = value.traces;
            return true;
          }
          return false;
        
        default:
          return false;
      }
    } catch (error) {
      console.error('Failed to apply data update:', error);
      return false;
    }
  };

  const applyBatchUpdate = (plotlyJson: any, path: string, operations: any): boolean => {
    if (path !== 'apply' || !Array.isArray(operations)) {
      console.warn('⚠️ Unknown batch update payload', { path, operations });
      return false;
    }

    let success = true;
    for (const op of operations) {
      if (!op || typeof op.type !== 'string') {
        console.warn('⚠️ Skipping invalid batch operation', op);
        continue;
      }
      const applied = applyUpdate(plotlyJson, op.type, op.value);
      success = success && applied;
    }

    return success;
  };

  // Overlay updates (shapes, regions)
  const applyOverlayUpdate = (plotlyJson: any, path: string, value: any): boolean => {
    if (!plotlyJson.layout.shapes) plotlyJson.layout.shapes = [];
    
    console.log('🔷 applyOverlayUpdate called:', { path, value, currentShapes: plotlyJson.layout.shapes.length });
    
    if (path === 'add') {
      plotlyJson.layout.shapes.push(value);
      console.log('✅ Shape added! New count:', plotlyJson.layout.shapes.length);
      console.log('📊 Updated shapes:', plotlyJson.layout.shapes);
    } else if (path.startsWith('remove.')) {
      const index = parseInt(path.split('.')[1]);
      plotlyJson.layout.shapes.splice(index, 1);
      console.log('🗑️ Shape removed at index:', index);
    } else if (path.startsWith('update.')) {
      const [_, index, ...propertyParts] = path.split('.');
      const shapeIndex = parseInt(index);
      const property = propertyParts.join('.');
      if (plotlyJson.layout.shapes[shapeIndex]) {
        setNestedProperty(plotlyJson.layout.shapes[shapeIndex], property, value);
        console.log('✏️ Shape updated:', { index: shapeIndex, property, value });
      }
    }
    
    return true;
  };

  // Annotation updates
  const applyAnnotationUpdate = (plotlyJson: any, path: string, value: any): boolean => {
    if (!plotlyJson.layout.annotations) plotlyJson.layout.annotations = [];
    
    // Ensure annotation defaults don't re-introduce backgrounds
    if (plotlyJson.layout.annotationdefaults?.bgcolor &&
        plotlyJson.layout.annotationdefaults.bgcolor !== 'rgba(0,0,0,0)') {
      plotlyJson.layout.annotationdefaults.bgcolor = 'rgba(0,0,0,0)';
    }
    if (plotlyJson.layout.annotationdefaults?.bordercolor &&
        plotlyJson.layout.annotationdefaults.bordercolor !== 'rgba(0,0,0,0)') {
      plotlyJson.layout.annotationdefaults.bordercolor = 'rgba(0,0,0,0)';
    }
    
    console.log('📝 applyAnnotationUpdate called:', { path, value, currentAnnotations: plotlyJson.layout.annotations.length });
    
    if (path === 'add') {
      plotlyJson.layout.annotations.push(value);
      console.log('✅ Annotation added! New count:', plotlyJson.layout.annotations.length);
      console.log('📊 Updated annotations:', plotlyJson.layout.annotations);
    } else if (path.startsWith('remove.')) {
      const index = parseInt(path.split('.')[1]);
      plotlyJson.layout.annotations.splice(index, 1);
      console.log('🗑️ Annotation removed at index:', index);
    } else if (path.startsWith('update.')) {
      const [_, index, ...propertyParts] = path.split('.');
      const annotationIndex = parseInt(index);
      const property = propertyParts.join('.');
      
      if (plotlyJson.layout.annotations[annotationIndex]) {
        const annotation = plotlyJson.layout.annotations[annotationIndex];
        
        // Handle batch updates atomically
        if (property === 'batch' && typeof value === 'object') {
          console.log('⚛️ Processing atomic batch update:', value);
          // Apply all properties at once
          Object.keys(value).forEach(key => {
            if (key === 'bgcolor' && value[key] === 'rgba(0,0,0,0)') {
              // Delete bgcolor when transparent
              delete annotation.bgcolor;
              console.log('🗑️ Deleted bgcolor property');
            } else if (key === 'bordercolor' && value[key] === 'rgba(0,0,0,0)') {
              // Delete bordercolor when transparent
              delete annotation.bordercolor;
              console.log('🗑️ Deleted bordercolor property');
            } else if (key === 'borderwidth' && value[key] === 0) {
              // Delete borderwidth when 0
              delete annotation.borderwidth;
              console.log('🗑️ Deleted borderwidth property');
            } else if (key === 'borderpad' && value[key] === 0) {
              // Delete borderpad when 0
              delete annotation.borderpad;
              console.log('🗑️ Deleted borderpad property');
            } else {
              setNestedProperty(annotation, key, value[key]);
            }
          });
          console.log('✅ Batch update applied:', { index: annotationIndex, updates: value, annotation: {...annotation} });
        } else {
          // Single property update
          setNestedProperty(annotation, property, value);
          console.log('✏️ Annotation updated:', { index: annotationIndex, property, value });
        }
      }
    }
    
    return true;
  };

  // Design/theme updates
  const applyDesignUpdate = (plotlyJson: any, path: string, value: any): boolean => {
    switch (path) {
      case 'design.colorPalette': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        const meta:any = (plotlyJson.layout.meta ||= {});
        const lumi:any = (meta.lumi ||= {});
        const theme:any = (lumi.theme ||= {});
        const row: string[] = Array.isArray(value) ? value : [];

        // 1) Persist in meta (categorical row)
        theme.categorical = row;
        // Compatibility: mirror into swatches for legacy consumers
        theme.swatches = Array.isArray(row) ? row.slice() : [];

        // 2) Apply to layout colorways (always overwrite)
        if (Array.isArray(row) && row.length > 0) {
          plotlyJson.layout.colorway = row.slice();
          (plotlyJson.layout as any).sunburstcolorway = row.slice();
          (plotlyJson.layout as any).treemapcolorway = row.slice();
          (plotlyJson.layout as any).iciclecolorway = row.slice();
          (plotlyJson.layout as any).funnelareacolorway = row.slice();
          (plotlyJson.layout as any).extendsunburstcolors = true;
        }

        // 3) Optional ordered mapping if domain is known (preserve locked entries)
        try {
          const domain: (string|number)[] = Array.isArray((theme as any)?.mappings?.domain) ? (theme as any).mappings.domain : [];
          const lockedSet = new Set((((theme as any)?.mappings?.locked) || []).map((v: any) => String(v)));
          const existingColors = ((theme as any)?.mappings?.colors) || {};
          if (Array.isArray(domain) && domain.length > 0 && row.length > 0) {
            const nextColors: Record<string, string> = { ...existingColors };
            domain.forEach((cat: any, i: number) => {
              const key = String(cat);
              if (lockedSet.has(key) && existingColors[key]) return;
              nextColors[key] = row[i % row.length];
            });
            theme.mappings = { ...(theme.mappings || {}), colors: nextColors };
          }
        } catch {}

        // 4) Bump editrevision; do NOT touch trace data
        // The editrevision bump will trigger plot-canvas to reapply theme colors
        // For Sankey traces, theme colors will be reapplied automatically via plot-canvas logic
        (plotlyJson.layout as any).editrevision = ((plotlyJson.layout as any).editrevision || 0) + 1;
        console.log('🎨 Applied categorical palette', { count: row.length });
        return true;
      }
      case 'design.theme.range.min': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        const theme:any = (((plotlyJson.layout.meta ||= {}) as any).lumi ||= {}).theme ||= {};
        const mode = theme.mode || 'sequential';
        if (mode === 'diverging') {
          theme.diverging ||= {}; theme.diverging.min = value;
        } else { theme.sequential ||= {}; theme.sequential.min = value; }
        // when custom bounds set, ensure cauto=false
        (plotlyJson.layout as any).coloraxis = { ...(plotlyJson.layout as any).coloraxis, cauto: false };
        return true;
      }
      case 'design.theme.range.max': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        const theme:any = (((plotlyJson.layout.meta ||= {}) as any).lumi ||= {}).theme ||= {};
        const mode = theme.mode || 'sequential';
        if (mode === 'diverging') {
          theme.diverging ||= {}; theme.diverging.max = value;
        } else { theme.sequential ||= {}; theme.sequential.max = value; }
        (plotlyJson.layout as any).coloraxis = { ...(plotlyJson.layout as any).coloraxis, cauto: false };
        return true;
      }
      case 'design.theme.midpoint': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        const theme:any = (((plotlyJson.layout.meta ||= {}) as any).lumi ||= {}).theme ||= {};
        theme.diverging ||= {}; theme.diverging.midpoint = value;
        return true;
      }
      case 'design.theme.clamp': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        const theme:any = (((plotlyJson.layout.meta ||= {}) as any).lumi ||= {}).theme ||= {};
        theme.notes = { ...(theme.notes || {}), clamp: !!value };
        return true;
      }
      case 'design.theme.nan': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        const theme:any = (((plotlyJson.layout.meta ||= {}) as any).lumi ||= {}).theme ||= {};
        const id = theme.coloraxis || 'coloraxis';
        (plotlyJson.layout as any)[id] = { ...(plotlyJson.layout as any)[id], nancolor: value };
        return true;
      }
      case 'design.theme.missingCategoryColor': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        const theme:any = (((plotlyJson.layout.meta ||= {}) as any).lumi ||= {}).theme ||= {};
        theme.mappings ||= { by: null, domain: [], colors: {}, locked: [] };
        (theme.mappings as any).missing = value;
        return true;
      }
      case 'theme.categorical': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        const meta:any = (plotlyJson.layout.meta ||= {});
        const lumi:any = (meta.lumi ||= {});
        const theme:any = (lumi.theme ||= {});
        const row: string[] = Array.isArray(value) ? value : [];

        // 1) Persist in meta (categorical row)
        theme.categorical = row;
        // Compatibility: mirror into swatches for legacy consumers
        theme.swatches = Array.isArray(row) ? row.slice() : [];

        // 2) Apply to layout colorways (always overwrite)
        if (Array.isArray(row) && row.length > 0) {
          plotlyJson.layout.colorway = row.slice();
          (plotlyJson.layout as any).sunburstcolorway = row.slice();
          (plotlyJson.layout as any).treemapcolorway = row.slice();
          (plotlyJson.layout as any).iciclecolorway = row.slice();
          (plotlyJson.layout as any).funnelareacolorway = row.slice();
          (plotlyJson.layout as any).extendsunburstcolors = true;
        }

        // 3) Optional ordered mapping if domain is known (preserve locked entries)
        try {
          const domain: (string|number)[] = Array.isArray((theme as any)?.mappings?.domain) ? (theme as any).mappings.domain : [];
          const lockedSet = new Set((((theme as any)?.mappings?.locked) || []).map((v: any) => String(v)));
          const existingColors = ((theme as any)?.mappings?.colors) || {};
          if (Array.isArray(domain) && domain.length > 0 && row.length > 0) {
            const nextColors: Record<string, string> = { ...existingColors };
            domain.forEach((cat: any, i: number) => {
              const key = String(cat);
              if (lockedSet.has(key) && existingColors[key]) return;
              nextColors[key] = row[i % row.length];
            });
            theme.mappings = { ...(theme.mappings || {}), colors: nextColors };
          }
        } catch {}

        // 4) Bump editrevision; do NOT touch trace data
        (plotlyJson.layout as any).editrevision = ((plotlyJson.layout as any).editrevision || 0) + 1;
        console.log('🎨 Applied categorical palette', { count: row.length });
        return true;
      }
      case 'theme.continuous': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        if (!plotlyJson.layout.meta) plotlyJson.layout.meta = {} as any;
        if (!(plotlyJson.layout.meta as any).lumi) (plotlyJson.layout.meta as any).lumi = {} as any;
        if (!((plotlyJson.layout.meta as any).lumi as any).theme) ((plotlyJson.layout.meta as any).lumi as any).theme = {} as any;
        // Persist chosen continuous colorscale name canonically on sequential.scale
        const theme:any = (((plotlyJson.layout.meta as any).lumi as any).theme) as any;
        theme.sequential = { ...(theme.sequential || {}), scale: String(value || 'Viridis') };
        // Keep legacy field updated for backward consumers
        theme.continuous = String(value || 'Viridis');
        // Update coloraxis to reference the named scale and preserve reversescale
        try {
          const reversed = !!(theme?.sequential?.reversed || theme?.diverging?.reversed);
          (plotlyJson.layout as any).coloraxis = { ...(plotlyJson.layout as any).coloraxis, colorscale: String(value || 'Viridis'), reversescale: reversed };
          // Update any existing additional coloraxes similarly
          Object.keys(plotlyJson.layout || {}).forEach((k: string) => {
            if (k === 'coloraxis' || /^coloraxis\d+$/.test(k)) {
              const ca:any = (plotlyJson.layout as any)[k];
              if (ca) (plotlyJson.layout as any)[k] = { ...ca, colorscale: String(value || 'Viridis'), reversescale: reversed };
            }
          });
        } catch {}
        return true;
      }
      case 'design.theme.shareColoraxis': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        if (!plotlyJson.layout.meta) plotlyJson.layout.meta = {} as any;
        if (!(plotlyJson.layout.meta as any).lumi) (plotlyJson.layout.meta as any).lumi = {} as any;
        if (!((plotlyJson.layout.meta as any).lumi as any).theme) ((plotlyJson.layout.meta as any).lumi as any).theme = {} as any;
        const theme:any = ((plotlyJson.layout.meta as any).lumi as any).theme as any;
        theme.coloraxis = value ? 'coloraxis' : undefined;
        return true;
      }
      case 'design.theme.roles.increasing': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        const theme:any = (((plotlyJson.layout.meta ||= {}) as any).lumi ||= {}).theme ||= {};
        theme.roles = { ...(theme.roles || {}), increasing: value };
        return true;
      }
      case 'design.theme.roles.decreasing': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        const theme:any = (((plotlyJson.layout.meta ||= {}) as any).lumi ||= {}).theme ||= {};
        theme.roles = { ...(theme.roles || {}), decreasing: value };
        return true;
      }
      case 'design.theme.roles.totals': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        const theme:any = (((plotlyJson.layout.meta ||= {}) as any).lumi ||= {}).theme ||= {};
        theme.roles = { ...(theme.roles || {}), totals: value };
        return true;
      }
      case 'design.theme.assignNewCategories': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        const theme:any = (((plotlyJson.layout.meta ||= {}) as any).lumi ||= {}).theme ||= {};
        const t = ensureThemeDefaults(theme as any);
        const existingDomain: (string|number)[] = Array.isArray((t as any).mappings?.domain) ? (t as any).mappings.domain : [];
        const incoming: (string|number)[] = Array.isArray(value) ? value : [];
        // Merge domain preserving order
        const merged: (string|number)[] = [...existingDomain];
        for (const v of incoming) {
          const key = String(v);
          if (!merged.map(String).includes(key)) merged.push(v);
        }

        // Choose palette row: prefer categorical, fall back to swatches
        const row: string[] = Array.isArray((t as any).categorical) && (t as any).categorical.length
          ? (t as any).categorical
          : (Array.isArray((t as any).swatches) ? (t as any).swatches : []);

        // Ordered mapping: rebuild colors by domain order, preserving locked entries
        const lockedSet = new Set((((t as any).mappings?.locked) || []).map((v: any) => String(v)));
        const existingColors = ((t as any).mappings?.colors) || {};
        let nextColors: Record<string, string> = { ...existingColors };
        if (Array.isArray(merged) && merged.length > 0 && row.length > 0) {
          nextColors = { ...existingColors };
          merged.forEach((cat: any, i: number) => {
            const key = String(cat);
            if (lockedSet.has(key) && existingColors[key]) return;
            nextColors[key] = row[i % row.length];
          });
        }

        theme.mappings = { ...(t as any).mappings, domain: merged, colors: nextColors };
        // Pattern fallback prompt when > 20
        if (merged.length > 20) {
          theme.pattern = { ...(theme.pattern || {}), enabled: true };
        }
        return true;
      }
      case 'design.theme.lockValue': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        const theme:any = (((plotlyJson.layout.meta ||= {}) as any).lumi ||= {}).theme ||= {};
        theme.mappings = theme.mappings || { by: null, domain: [], colors: {}, locked: [] };
        const key = String((value?.key ?? ''));
        if (key) {
          if (!Array.isArray(theme.mappings.domain)) theme.mappings.domain = [];
          if (!theme.mappings.domain.map(String).includes(key)) theme.mappings.domain.push(key);
          theme.mappings.colors = { ...(theme.mappings.colors || {}), [key]: value?.color || (theme.swatches?.[0] || '#1f77b4') };
          const exists = (theme.mappings.locked || []).map(String).includes(key);
          theme.mappings.locked = value?.locked ? (exists ? theme.mappings.locked : [...(theme.mappings.locked || []), key]) : (theme.mappings.locked || []).filter((v:any) => String(v) !== key);
        }
        return true;
      }
      case 'normalizeColors': {
        try {
          const isNumericArray = (arr: any): boolean => Array.isArray(arr) && arr.length > 0 && arr.every((v) => typeof v === 'number' && isFinite(v));
          const hasZ = (tr: any) => tr && tr.z && (Array.isArray(tr.z) || Array.isArray(tr.z?.[0]));
          (plotlyJson.layout as any).coloraxis = (plotlyJson.layout as any).coloraxis || {};
          if (Array.isArray(plotlyJson.data)) {
            (plotlyJson.data as any[]).forEach((tr: any) => {
              const t = String((tr?.type || 'scatter')).toLowerCase();
              const zScaleTypes = new Set(['heatmap', 'contour', 'histogram2d', 'histogram2dcontour', 'densitymapbox', 'choropleth', 'choroplethmapbox', 'image']);
              // Handle z-based traces: both known z-scale types AND any trace with z data
              if (hasZ(tr)) {
                // For known z-scale types, bind to coloraxis
                if (zScaleTypes.has(t)) {
                  tr.coloraxis = tr.coloraxis || 'coloraxis';
                  if (tr.colorscale) delete tr.colorscale;
                  if (tr.autocolorscale) delete tr.autocolorscale;
                } else {
                  // For other trace types with z data (e.g., scatter3d, surface), also bind to coloraxis
                  // This ensures z data color control works for all trace types
                  tr.coloraxis = tr.coloraxis || 'coloraxis';
                  if (tr.colorscale) delete tr.colorscale;
                  if (tr.autocolorscale) delete tr.autocolorscale;
                }
              }
              const markerScaleTypes = new Set(['scatter', 'scattergl', 'scattergeo', 'scattermapbox', 'splom', 'bar']);
              if (markerScaleTypes.has(t) && tr.marker && isNumericArray(tr.marker.color)) {
                tr.marker.coloraxis = tr.marker.coloraxis || 'coloraxis';
                if (tr.marker.colorscale) delete tr.marker.colorscale;
              }
            });
            plotlyJson.data = [...plotlyJson.data];
          }
        } catch {}
        return true;
      }
      case 'theme.reset': {
        // Strip explicit per-trace colors so theme can reapply
        try {
          const layout:any = plotlyJson.layout || {};
          const theme:any = (layout?.meta as any)?.lumi?.theme || {};
          const categorical:string[]|undefined = Array.isArray(theme.categorical) ? theme.categorical : undefined;
          const isStringArray = (arr: any): boolean => Array.isArray(arr) && arr.length > 0 && arr.every((v) => typeof v === 'string');
          if (Array.isArray(plotlyJson.data)) {
            (plotlyJson.data as any[]).forEach((tr: any) => {
              // Remove explicit categorical colors but preserve numeric mappings
              if (tr.marker) {
                if (typeof tr.marker.color === 'string' || isStringArray(tr.marker.color)) delete tr.marker.color;
                // keep numeric marker.color arrays
                if (tr.marker.colors) delete tr.marker.colors;
                if (tr.marker.line && tr.marker.line.color) delete tr.marker.line.color;
                if (tr.marker.colorscale) delete tr.marker.colorscale;
              }
              if (tr.line && tr.line.color) delete tr.line.color;
              if (tr.fillcolor) delete tr.fillcolor;
              if (tr.colorscale) delete tr.colorscale;
              // preserve coloraxis so continuous scales continue to work
              // if (tr.coloraxis) delete tr.coloraxis;
              if (tr.node && tr.node.color) delete tr.node.color;
              if (tr.increasing && tr.increasing.line && tr.increasing.line.color) delete tr.increasing.line.color;
              if (tr.decreasing && tr.decreasing.line && tr.decreasing.line.color) delete tr.decreasing.line.color;
              if (tr.increasing && tr.increasing.marker && tr.increasing.marker.color) delete tr.increasing.marker.color;
              if (tr.decreasing && tr.decreasing.marker && tr.decreasing.marker.color) delete tr.decreasing.marker.color;
              if (tr.totals && tr.totals.marker && tr.totals.marker.color) delete tr.totals.marker.color;
            });
            // Force data ref change
            plotlyJson.data = [...plotlyJson.data];
          }
          // Clear layout coloraxis colorscales so continuous theme can apply
          Object.keys(plotlyJson.layout || {}).forEach((k: string) => {
            if (k === 'coloraxis' || /^coloraxis\d+$/.test(k)) {
              const ca:any = (plotlyJson.layout as any)[k];
              if (ca && ca.colorscale) delete ca.colorscale;
            }
          });
          // If categorical palette exists, ensure colorway is set
          if (categorical && categorical.length > 0) {
            plotlyJson.layout.colorway = categorical;
          }
        } catch {}
        return true;
      }
      case 'theme':
        applyTheme(plotlyJson, value);
        break;
      case 'applyTemplate': {
        try {
          // Skip size changes in standalone plot editor - only apply typography/styling
          // This prevents plots from changing size when switching journal templates (bad UX)
          // Size normalization happens in figure composition context instead
          const result = applyJournalTemplate(plotlyJson, value, { skipSizeChanges: true });
          if (!plotlyJson.layout) plotlyJson.layout = {};
          (plotlyJson.layout as any)._publication = {
            activeTemplateId: value,
            previousStyleState: result.previous
          };
          // Per-plot publication overrides (do not change the template definitions)
          if (value === 'science_advances') {
            // Force compact figure size: width policy already set by template; set height to 55mm
            const targetH = mmToPx(55);
            plotlyJson.layout.height = targetH;
            // Compact margins for small figures so plotting area isn't squeezed
            const m:any = plotlyJson.layout.margin || {};
            plotlyJson.layout.margin = {
              t: Math.min(24, m.t ?? 24),
              r: Math.min(16, m.r ?? 16),
              b: Math.min(28, m.b ?? 28),
              l: Math.min(36, m.l ?? 36)
            } as any;
            if (Array.isArray(plotlyJson.data)) {
              for (const tr of plotlyJson.data as any[]) {
                if (!tr.line) tr.line = {};
                tr.line.width = 0.5; // 0.5 pt≈px for Plotly
                if (tr.marker) {
                  if (!tr.marker.line) tr.marker.line = {};
                  tr.marker.line.width = 0.5;
                }
              }
            }
            // Let canvas auto-fit preview scale
            (plotlyJson.layout as any)._previewScale = 'auto';
            // Legend: compact horizontal legend above plot to avoid crowding
            if (!plotlyJson.layout.legend) plotlyJson.layout.legend = {} as any;
            Object.assign(plotlyJson.layout.legend as any, {
              orientation: 'h', x: 0, xanchor: 'left', y: 1.02, yanchor: 'bottom',
              font: { ...(plotlyJson.layout.legend as any).font, size: 6 },
              borderwidth: 0, bgcolor: 'rgba(0,0,0,0)', itemsizing: 'constant', itemwidth: 30
            });
          }
          if (value === 'science') {
            if (!plotlyJson.layout.font) plotlyJson.layout.font = {} as any;
            (plotlyJson.layout.font as any).size = 6; // 6 pt
            // Force height to 55mm for the actual plot (not template)
            const targetH = mmToPx(55);
            plotlyJson.layout.height = targetH;
            // Compact margins for small figures
            const m2:any = plotlyJson.layout.margin || {};
            plotlyJson.layout.margin = {
              t: Math.min(24, m2.t ?? 24),
              r: Math.min(16, m2.r ?? 16),
              b: Math.min(28, m2.b ?? 28),
              l: Math.min(36, m2.l ?? 36)
            } as any;
            if (Array.isArray(plotlyJson.data)) {
              for (const tr of plotlyJson.data as any[]) {
                if (!tr.line) tr.line = {};
                tr.line.width = 0.5;
                if (tr.marker) {
                  if (!tr.marker.line) tr.marker.line = {};
                  tr.marker.line.width = 0.5;
                }
              }
            }
            // Let canvas auto-fit preview scale
            (plotlyJson.layout as any)._previewScale = 'auto';
            // Legend: horizontal and compact
            if (!plotlyJson.layout.legend) plotlyJson.layout.legend = {} as any;
            Object.assign(plotlyJson.layout.legend as any, {
              orientation: 'h', x: 0, xanchor: 'left', y: 1.02, yanchor: 'bottom',
              font: { ...(plotlyJson.layout.legend as any).font, size: 6 },
              borderwidth: 0, bgcolor: 'rgba(0,0,0,0)', itemsizing: 'constant', itemwidth: 30
            });
          }
          if (value === 'nature') {
            // For Nature small-column figures, set a minimum preview scale and compact margins
            if (!plotlyJson.layout.font) plotlyJson.layout.font = {} as any;
            if (!plotlyJson.layout.margin) plotlyJson.layout.margin = {} as any;
            const mn:any = plotlyJson.layout.margin;
            plotlyJson.layout.margin = {
              t: Math.min(28, mn.t ?? 28),
              r: Math.min(18, mn.r ?? 18),
              b: Math.min(34, mn.b ?? 34),
              l: Math.min(40, mn.l ?? 40)
            } as any;
            // Per-plot size override (do not edit template): 129 mm × 129 mm
            plotlyJson.layout.width = mmToPx(129);
            plotlyJson.layout.height = mmToPx(129);
            // Let canvas auto-fit, but ensure at least 1.5x for readability
            (plotlyJson.layout as any)._previewScale = Math.max(1.5, (plotlyJson.layout as any)._previewScale || 0);
            // Legend: horizontal top-left, minimum 8 pt to meet guideline, compact spacing
            if (!plotlyJson.layout.legend) plotlyJson.layout.legend = {} as any;
            const lf:any = ((plotlyJson.layout.legend as any).font ?? {});
            Object.assign(plotlyJson.layout.legend as any, {
              orientation: 'h', x: 0, xanchor: 'left', y: 1.02, yanchor: 'bottom',
              font: { ...lf, size: Math.max(8, lf.size || 8) },
              borderwidth: 0, bgcolor: 'rgba(0,0,0,0)', itemsizing: 'constant', itemwidth: 36, tracegroupgap: 0
            });
          }
          // Force editrevision bump handled by caller
        } catch (e) {
          console.warn('Template apply failed', e);
        }
        break;
      }
      case 'restorePrevious': {
        const meta:any = (plotlyJson.layout as any)?._publication;
        const prev:any = meta?.previousStyleState;
        if (prev) {
          if (!plotlyJson.layout) plotlyJson.layout = {};
          // Restore basic style and layout fields captured previously
          if (prev.width !== undefined) plotlyJson.layout.width = prev.width;
          if (prev.height !== undefined) plotlyJson.layout.height = prev.height;
          if (prev.font !== undefined) plotlyJson.layout.font = prev.font;
          if (prev.margin !== undefined) plotlyJson.layout.margin = prev.margin;
          if (prev.legend !== undefined) plotlyJson.layout.legend = prev.legend;
          if (prev.export !== undefined) (plotlyJson.layout as any)._export = prev.export;
          // Clear active template id after restore (one-level undo)
          (plotlyJson.layout as any)._publication = {
            activeTemplateId: null,
            previousStyleState: null
          };
        }
        break;
      }
      case 'footer.show': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        if (!plotlyJson.layout.annotations) plotlyJson.layout.annotations = [];
        const idx = (plotlyJson.layout.annotations as any[]).findIndex((a: any) => a && a._footer === true);
        if (value) {
          if (idx === -1) {
            plotlyJson.layout.annotations.push({
              _footer: true,
              xref: 'paper',
              yref: 'paper',
              x: 0.5,
              y: 0,
              xanchor: 'center',
              yanchor: 'top',
              text: (plotlyJson.layout as any)?.footer?.text || '',
              showarrow: false,
              align: 'center',
              font: { size: (plotlyJson.layout?.font?.size || 12) - 2, color: plotlyJson.layout?.font?.color || '#000' },
            });
          }
        } else if (idx !== -1) {
          plotlyJson.layout.annotations.splice(idx, 1);
        }
        break;
      }
      case 'footer.text': {
        if (!plotlyJson.layout) plotlyJson.layout = {};
        if (!plotlyJson.layout.annotations) plotlyJson.layout.annotations = [];
        let idx = (plotlyJson.layout.annotations as any[]).findIndex((a: any) => a && a._footer === true);
        if (idx === -1) {
          // Create footer annotation if missing
          plotlyJson.layout.annotations.push({
            _footer: true,
            xref: 'paper',
            yref: 'paper',
            x: 0.5,
            y: 0,
            xanchor: 'center',
            yanchor: 'top',
            text: value,
            showarrow: false,
            align: 'center',
            font: { size: (plotlyJson.layout?.font?.size || 12) - 2, color: plotlyJson.layout?.font?.color || '#000' },
          });
        } else {
          (plotlyJson.layout.annotations as any[])[idx].text = value;
        }
        break;
      }
      case 'font.family':
        if (!plotlyJson.layout.font) plotlyJson.layout.font = {};
        plotlyJson.layout.font.family = value;
        break;
      case 'font.size':
        if (!plotlyJson.layout.font) plotlyJson.layout.font = {};
        plotlyJson.layout.font.size = value;
        break;
      case 'font.color':
        if (!plotlyJson.layout.font) plotlyJson.layout.font = {};
        plotlyJson.layout.font.color = value;
        break;
      case 'bgcolor':
        plotlyJson.layout.paper_bgcolor = value;
        return true;
      case 'plotbgcolor':
        plotlyJson.layout.plot_bgcolor = value;
        return true;
      case 'legend.position': {
        if (!plotlyJson.layout.legend) plotlyJson.layout.legend = {};
        const [x, y] = value.split(',').map((v: string) => parseFloat(v));
        plotlyJson.layout.legend.x = x;
        plotlyJson.layout.legend.y = y;
        break;
      }
      case 'legend.width': {
        if (!plotlyJson.layout.legend) plotlyJson.layout.legend = {} as any;
        // Use uirevision-based wrapping: constrain tracegroupgap and itemwidth, and use legend.width for full-box width
        const v = Math.max(50, Number(value));
        (plotlyJson.layout.legend as any).itemwidth = v; // label swatch width baseline
        (plotlyJson.layout.legend as any).orientation = (plotlyJson.layout.legend as any).orientation || 'h';
        // Apply full legend box width via legend.xanchor/x/ and a synthetic width using paper coords (approximate)
        (plotlyJson.layout.legend as any).x = 0; (plotlyJson.layout.legend as any).xanchor = 'left';
        (plotlyJson.layout.legend as any).y = 1.02; (plotlyJson.layout.legend as any).yanchor = 'bottom';
        break;
      }
      case 'legend.rows': {
        // Force a target number of rows by computing an appropriate itemwidth
        const desiredRows = Math.max(1, parseInt(String(value)) || 1);
        if (!plotlyJson.layout.legend) plotlyJson.layout.legend = {} as any;
        (plotlyJson.layout.legend as any).orientation = 'h';

        try {
          const layout:any = plotlyJson.layout as any;
          const data:any[] = Array.isArray(plotlyJson.data) ? (plotlyJson.data as any[]) : [];
          const visibleCount = data.filter((t:any) => t?.visible !== false && String(t?.visible || '').toLowerCase() !== 'legendonly').length || 1;
          const widthPx = typeof layout.width === 'number' ? layout.width : 800;
          const ml = typeof layout?.margin?.l === 'number' ? layout.margin.l : 80;
          const mr = typeof layout?.margin?.r === 'number' ? layout.margin.r : 80;
          const contentWidth = Math.max(0, widthPx - ml - mr);
          const targetCols = Math.max(1, Math.ceil(visibleCount / desiredRows));
          const itemWidth = Math.max(50, Math.floor(contentWidth / targetCols));
          (layout.legend as any).itemwidth = itemWidth;
          // Keep legend positioned at the top; margin handling is controlled by reserveSpace
          (layout.legend as any).x = 0; (layout.legend as any).xanchor = 'left';
          (layout.legend as any).y = 1.02; (layout.legend as any).yanchor = 'bottom';
          // Remember preference (optional, used only for UX)
          (layout as any)._legendRowsPreferred = desiredRows;
        } catch {}
        break;
      }
      case 'legend.reserveSpace': {
        const enable = !!value;
        const layout:any = plotlyJson.layout as any;
        // Toggle a private flag so the UI can reflect current state
        (layout as any)._legendReserveSpace = enable;

        try {
          // Estimate rows to reserve space for
          const data:any[] = Array.isArray(plotlyJson.data) ? (plotlyJson.data as any[]) : [];
          const visibleCount = data.filter((t:any) => t?.visible !== false && String(t?.visible || '').toLowerCase() !== 'legendonly').length || 1;
          const widthPx = typeof layout.width === 'number' ? layout.width : 800;
          const ml = typeof layout?.margin?.l === 'number' ? layout.margin.l : 80;
          const mr = typeof layout?.margin?.r === 'number' ? layout.margin.r : 80;
          const contentWidth = Math.max(0, widthPx - ml - mr);
          const itemWidth = Math.max(50, Number((layout?.legend as any)?.itemwidth || 120));
          const cols = Math.max(1, Math.floor(contentWidth / itemWidth));
          const rows = Math.max(1, Math.ceil(visibleCount / cols));
          const fontSize = Math.max(8, Number((layout?.legend as any)?.font?.size || layout?.font?.size || 12));
          const rowHeight = Math.round(fontSize + 8);
          const extra = Math.max(0, rows * rowHeight + 6);

          if (!layout.margin) layout.margin = {} as any;
          const currentTop = Number(layout.margin.t || 0);

          if (enable) {
            // Increase top margin and record how much we added
            layout.margin.t = currentTop + extra;
            (layout as any)._legendReserveSpacePx = extra;
          } else {
            // Reduce top margin by the previously added amount
            const added = Number((layout as any)._legendReserveSpacePx || 0);
            if (added > 0) layout.margin.t = Math.max(0, currentTop - added);
            delete (layout as any)._legendReserveSpacePx;
          }
        } catch {}

        break;
      }
      case 'font.weight': {
        if (!plotlyJson.layout.font) plotlyJson.layout.font = {} as any;
        (plotlyJson.layout.font as any).weight = value;
        break;
      }
      case 'legend.font.color': {
        if (!plotlyJson.layout.legend) (plotlyJson.layout as any).legend = {};
        if (!(plotlyJson.layout.legend as any).font) (plotlyJson.layout.legend as any).font = {};
        (plotlyJson.layout.legend as any).font.color = value;
        // Also set title color to keep title and items consistent
        if (!(plotlyJson.layout.legend as any).title) (plotlyJson.layout.legend as any).title = {};
        if (!((plotlyJson.layout.legend as any).title as any).font) ((plotlyJson.layout.legend as any).title as any).font = {};
        ((plotlyJson.layout.legend as any).title as any).font.color = value;
        // Normalize legend item appearance: ensure all traces are fully opaque and visible
        if (Array.isArray(plotlyJson.data)) {
          for (const t of plotlyJson.data) {
            if (t && (t.visible === 'legendonly' || t.visible === 'legendOnly')) {
              t.visible = true;
            }
            if (typeof t?.opacity === 'number' && t.opacity < 1) {
              t.opacity = 1;
            }
          }
        }
        break;
      }
      case 'legend.fill.enabled': {
        if (!plotlyJson.layout.legend) plotlyJson.layout.legend = {} as any;
        (plotlyJson.layout.legend as any).bgcolor = value ? ((plotlyJson.layout.legend as any).bgcolor || '#ffffff') : 'rgba(0,0,0,0)';
        break;
      }
      case 'legend.fill.color': {
        if (!plotlyJson.layout.legend) plotlyJson.layout.legend = {} as any;
        (plotlyJson.layout.legend as any).bgcolor = value;
        break;
      }
      case 'legend.outline.enabled': {
        if (!plotlyJson.layout.legend) plotlyJson.layout.legend = {} as any;
        (plotlyJson.layout.legend as any).bordercolor = value ? ((plotlyJson.layout.legend as any).bordercolor || '#000000') : 'rgba(0,0,0,0)';
        (plotlyJson.layout.legend as any).borderwidth = value ? 1 : 0;
        break;
      }
      case 'legend.outline.color': {
        if (!plotlyJson.layout.legend) plotlyJson.layout.legend = {} as any;
        (plotlyJson.layout.legend as any).bordercolor = value;
        (plotlyJson.layout.legend as any).borderwidth = ((plotlyJson.layout.legend as any).borderwidth ?? 0) > 0 ? (plotlyJson.layout.legend as any).borderwidth : 1;
        break;
      }
      case 'legend.outline': {
        if (!plotlyJson.layout.legend) plotlyJson.layout.legend = {};
        (plotlyJson.layout.legend as any).bordercolor = value;
        break;
      }
      case 'palette.opacity': {
        const opacity = Math.max(0, Math.min(1, Number(value)));
        // Persist on layout for later sessions/exports
        (plotlyJson.layout as any)._paletteOpacity = opacity;
        if (Array.isArray(plotlyJson.data)) {
          for (const t of plotlyJson.data) {
            const type = String(t?.type || 'scatter').toLowerCase();
            const mode: string = String(t?.mode || '');
            const zScaleTypes = new Set(['heatmap','contour','histogram2d','histogram2dcontour','image','densitymapbox','choropleth','choroplethmapbox']);
            const isLineOnly = (type === 'scatter' || type === 'scattergl') && mode.includes('lines') && !mode.includes('markers') && !t.fill;

            // Helper: only apply if unset or previously theme-applied
            const ensureThemeMeta = () => { (t as any)._lumiTheme = (t as any)._lumiTheme || {}; return (t as any)._lumiTheme; };
            const shouldApply = (current: any, flagKey: string) => current === undefined || ensureThemeMeta()[flagKey] === true;
            const markApplied = (flagKey: string) => { ensureThemeMeta()[flagKey] = true; };

            // Marker-bearing traces (bars, scatters, etc.): set marker.opacity only if not user-set
            if (type === 'bar' || type === 'histogram' || type === 'funnel' || type === 'waterfall' || (t.marker && (mode.includes('markers') || type !== 'scatter'))) {
              if (!t.marker) t.marker = {};
              if (shouldApply(t.marker.opacity, 'markerOpacity')) {
                (t.marker as any).opacity = opacity;
                markApplied('markerOpacity');
              }
            }

            // Area fills on scatters: trace.opacity only if not user-set
            if ((type === 'scatter' || type === 'scattergl') && t.fill && t.fill !== 'none') {
              if (shouldApply(t.opacity, 'traceOpacity')) {
                t.opacity = opacity;
                markApplied('traceOpacity');
              }
            }

            // Box/Violin/Pie/Funnelarea: use trace.opacity only if not user-set
            if (type === 'box' || type === 'violin' || type === 'pie' || type === 'funnelarea') {
              if (shouldApply(t.opacity, 'traceOpacity')) {
                t.opacity = opacity;
                markApplied('traceOpacity');
              }
            }

            // Never dim z-based image-like traces
            if (zScaleTypes.has(type)) {
              if ((t as any)._lumiTheme?.traceOpacity) delete t.opacity;
            }

            // Keep line-only traces fully opaque
            if (isLineOnly) {
              if ((t as any)._lumiTheme?.traceOpacity) delete t.opacity;
            }
          }
        }
        break;
      }
      case 'preview.scale': {
        const s = Math.max(1, Math.min(3, Number(value)));
        (plotlyJson.layout as any)._previewScale = s;
        break;
      }
      case 'margin':
        plotlyJson.layout.margin = value;
        break;
      case 'showlegend':
        plotlyJson.layout.showlegend = value;
        break;
      case 'colorPalette':
        // Implement the Lumiplot Color Engine (Analyze → Select → Verify)
        if (Array.isArray(value)) {
          const palette: string[] = value;
          const traces: any[] = Array.isArray(plotlyJson.data) ? plotlyJson.data : [];
          // Handle single bar trace with multiple categorical values by coloring each bar
          let singleBarPointColors: string[] | null = null;
          try {
            if (traces.length === 1) {
              const tr = traces[0] || {};
              const t = String(tr?.type || 'scatter').toLowerCase();
              const x = tr?.x; const y = tr?.y;
              const isStrArr = (arr: any) => Array.isArray(arr) && arr.length > 1 && arr.every((v:any)=> typeof v === 'string');
              if (t === 'bar' && (isStrArr(x) || isStrArr(y))) {
                const count = isStrArr(x) ? (x as any[]).length : (y as any[]).length;
                singleBarPointColors = Array.from({ length: count }, (_, i) => palette[i % palette.length]);
              }
            }
          } catch {}
          const n: number = singleBarPointColors ? singleBarPointColors.length : Math.max(1, traces.length || 1);

          // ---------- Conversions ----------
          const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
            const h = hex.replace('#', '');
            const v = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
            return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255 };
          };
          const srgbToLinear = (c: number) => {
            const cs = c / 255;
            return cs <= 0.04045 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
          };
          const rgbToXyz = ({ r, g, b }: { r: number; g: number; b: number }) => {
            const R = srgbToLinear(r), G = srgbToLinear(g), B = srgbToLinear(b);
            return {
              x: R * 0.4124 + G * 0.3576 + B * 0.1805,
              y: R * 0.2126 + G * 0.7152 + B * 0.0722,
              z: R * 0.0193 + G * 0.1192 + B * 0.9505
            };
          };
          const linRgbToXyz = (R: number, G: number, B: number) => {
            return {
              x: R * 0.4124 + G * 0.3576 + B * 0.1805,
              y: R * 0.2126 + G * 0.7152 + B * 0.0722,
              z: R * 0.0193 + G * 0.1192 + B * 0.9505
            };
          };
          const xyzToLab = ({ x, y, z }: { x: number; y: number; z: number }) => {
            const Xn = 0.95047, Yn = 1, Zn = 1.08883;
            const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
            const fx = f(x / Xn), fy = f(y / Yn), fz = f(z / Zn);
            const L = 116 * fy - 16, a = 500 * (fx - fy), b = 200 * (fy - fz);
            return { L, a, b };
          };
          const toLab = (hex: string) => xyzToLab(rgbToXyz(hexToRgb(hex)));
          const toLch = (lab: { L: number; a: number; b: number }) => {
            const C = Math.sqrt(lab.a * lab.a + lab.b * lab.b);
            let h = Math.atan2(lab.b, lab.a) * 180 / Math.PI;
            if (h < 0) h += 360;
            return { L: lab.L, C, h };
          };
          const dE = (c1: { L: number; a: number; b: number }, c2: { L: number; a: number; b: number }) => {
            const dL = c1.L - c2.L, da = c1.a - c2.a, db = c1.b - c2.b;
            return Math.sqrt(dL * dL + da * da + db * db);
          };
          // CVD simulation (Machado et al.) on linear RGB
          const cvdMatrix = {
            protanopia: [
              [0.152286, 1.052583, -0.204868],
              [0.114503, 0.786281, 0.099216],
              [-0.003882, -0.048116, 1.051998]
            ],
            deuteranopia: [
              [0.367322, 0.860646, -0.227968],
              [0.280085, 0.672501, 0.047413],
              [-0.011820, 0.042940, 0.968881]
            ]
          } as const;
          const simulateCvdLab = (hex: string, type: 'protanopia'|'deuteranopia') => {
            const { r, g, b } = hexToRgb(hex);
            const R = srgbToLinear(r), G = srgbToLinear(g), B = srgbToLinear(b);
            const M = cvdMatrix[type];
            const Rp = clamp(M[0][0]*R + M[0][1]*G + M[0][2]*B, 0, 1);
            const Gp = clamp(M[1][0]*R + M[1][1]*G + M[1][2]*B, 0, 1);
            const Bp = clamp(M[2][0]*R + M[2][1]*G + M[2][2]*B, 0, 1);
            const xyz = linRgbToXyz(Rp, Gp, Bp);
            return xyzToLab(xyz);
          };

          // ---------- Stage A: Analyze ----------
          const meta = palette.map((hex, idx) => {
            const lab = toLab(hex);
            const hcl = toLch(lab);
            const is_extreme = (hcl.L < 18 || hcl.L > 94 || hcl.C > 95);
            return { hex, lab, hcl, is_extreme, idx };
          });
          const hueVals = meta.map(m => m.hcl.h);
          const lVals = meta.map(m => m.lab.L);
          const hueMean = hueVals.reduce((a,b)=>a+b,0)/Math.max(1,hueVals.length);
          const hueVar = hueVals.reduce((a,b)=>a+Math.pow(((b-hueMean+540)%360-180),2),0)/Math.max(1,hueVals.length);
          // L monotonicity ratio
          let inc=0,dec=0; for(let i=1;i<lVals.length;i++){ if(lVals[i]>=lVals[i-1]) inc++; else dec++; }
          const mono = Math.max(inc,dec)/Math.max(1,(lVals.length-1));
          // Diverging heuristic: two hue clusters and low chroma center near middle
          const centerIdx = meta.reduce((best, m, i)=> (m.hcl.C < meta[best].hcl.C ? i : best), 0);
          const centerNearMid = Math.abs(centerIdx - Math.floor(meta.length/2)) <= 1;
          const hueRange = (()=>{
            let min=360,max=0; for(const h of hueVals){ min=Math.min(min,h); max=Math.max(max,h); }
            return max-min;
          })();
          let inferredType: 'seq'|'div'|'qual' = 'qual';
          if (hueVar < 800 && mono >= 0.7) inferredType = 'seq';
          if (hueRange > 160 && centerNearMid && meta[centerIdx].hcl.L >= Math.max(...lVals)*0.9) inferredType = 'div';

          // ---------- Stage B: Select ----------
          const clamp = (x:number,min:number,max:number)=>Math.min(Math.max(x,min),max);
          const sampleEquidistant = (labs: {L:number;a:number;b:number}[], k:number, trim=0.08) => {
            const segLen:number[]=[]; let total=0; for(let i=1;i<labs.length;i++){ const d=dE(labs[i-1],labs[i]); segLen.push(d); total+=d; }
            if(total===0){ return new Array(k).fill(labs[0]); }
            const start = total*trim, end = total*(1-trim), span = Math.max(1e-6,end-start);
            const targets = Array.from({length:k},(_,i)=> start + (i*(span)/(k-1||1)) );
            const out: {L:number;a:number;b:number}[]=[];
            for(const t of targets){
              let acc=0; for(let i=0;i<segLen.length;i++){ const l=segLen[i]; if(acc + l >= t){
                const ratio = (t-acc)/l; const p=labs[i], q=labs[i+1];
                out.push({ L: p.L+(q.L-p.L)*ratio, a: p.a+(q.a-p.a)*ratio, b: p.b+(q.b-p.b)*ratio }); break; }
                acc+=l; }
            }
            return out;
          };
          const labToHex = (lab:{L:number;a:number;b:number})=>{
            // Lab -> XYZ -> sRGB
            const Yn=1, Xn=0.95047, Zn=1.08883;
            const fy=(lab.L+16)/116; const fx=lab.a/500+fy; const fz=fy-lab.b/200;
            const inv=(t:number)=> (t**3>0.008856?t**3:(t-16/116)/7.787);
            const X=Xn*inv(fx), Y=Yn*inv(fy), Z=Zn*inv(fz);
            let r =  3.2406*X + (-1.5372)*Y + (-0.4986)*Z;
            let g = (-0.9689)*X +  1.8758*Y +  0.0415*Z;
            let b =  0.0557*X + (-0.2040)*Y +  1.0570*Z;
            const linToSrgb=(u:number)=> u<=0.0031308?12.92*u:1.055*Math.pow(u,1/2.4)-0.055;
            r=linToSrgb(r); g=linToSrgb(g); b=linToSrgb(b);
            const to255=(u:number)=> clamp(Math.round(u*255),0,255);
            const h=(n:number)=> n.toString(16).padStart(2,'0');
            return `#${h(to255(r))}${h(to255(g))}${h(to255(b))}`;
          };

          const selectQual = (hexes:string[], k:number):string[] => {
            if (k >= hexes.length) return [...hexes];
            const items = hexes.map((h,idx)=>{
              const lab = toLab(h); const hcl = toLch(lab);
              const lab_pro = simulateCvdLab(h, 'protanopia');
              const lab_deu = simulateCvdLab(h, 'deuteranopia');
              const extreme = (hcl.L<18||hcl.L>94);
              return { hex:h, lab, hcl, lab_pro, lab_deu, extreme, idx };
            });

            // Enumerate all subsets of size k (n<=6, M<=9 fast)
            const choose:number[][] = [];
            const M = items.length;
            const rec=(start:number, comb:number[])=>{
              if (comb.length===k){ choose.push([...comb]); return; }
              for(let i=start;i<=M-(k-comb.length);i++){ comb.push(i); rec(i+1, comb); comb.pop(); }
            };
            rec(0, []);

            const circSpread = (hs:number[])=>{
              const rad = hs.map(h=>h*Math.PI/180);
              const meanX = rad.reduce((a,b)=>a+Math.cos(b),0)/hs.length;
              const meanY = rad.reduce((a,b)=>a+Math.sin(b),0)/hs.length;
              const R = Math.sqrt(meanX*meanX+meanY*meanY);
              return 1-R; // larger is more spread
            };
            const std = (arr:number[])=>{
              const m = arr.reduce((a,b)=>a+b,0)/arr.length; return Math.sqrt(arr.reduce((a,b)=>a+(b-m)*(b-m),0)/arr.length);
            };

            const scoreSubset = (idxs:number[])=>{
              const subset = idxs.map(i=>items[i]);
              // pairwise minimum distances
              let minLab=Infinity, minCvd=Infinity;
              for(let i=0;i<subset.length;i++){
                for(let j=i+1;j<subset.length;j++){
                  minLab = Math.min(minLab, dE(subset[i].lab, subset[j].lab));
                  const dPro = dE(subset[i].lab_pro, subset[j].lab_pro);
                  const dDeu = dE(subset[i].lab_deu, subset[j].lab_deu);
                  minCvd = Math.min(minCvd, Math.min(dPro, dDeu));
                }
              }
              const hueSpread = circSpread(subset.map(s=>s.hcl.h));
              const cohesion = -(std(subset.map(s=>s.lab.L)) + 0.6*std(subset.map(s=>s.hcl.C)));
              const extremes = subset.reduce((c,s)=> c + (s.extreme?1:0), 0);
              return { minCvd, minLab, hueSpread, cohesion, extremes };
            };

            let best:number[]|null=null; let bestKey:number[] = [];
            for (const idxs of choose){
              const s = scoreSubset(idxs);
              const key = [s.minCvd, s.minLab, s.hueSpread, s.cohesion, -s.extremes];
              if (!best || lexGreater(key, bestKey)) { best = idxs; bestKey = key; }
            }
            function lexGreater(a:number[], b:number[]):boolean{ for(let i=0;i<a.length;i++){ if(a[i]>b[i]) return true; if(a[i]<b[i]) return false; } return false; }

            // Return in original palette order (tie-break preserved by search order)
            return (best ?? Array.from({length:k},(_,i)=>i)).map(i=>items[i].hex);
          };

          const selectSeq = (hexes:string[], k:number):string[] => {
            const labs = hexes.map(toLab);
            const samples = sampleEquidistant(labs, k, 0.08).map(l=>({ ...l, L: clamp(l.L,25,92) }));
            return samples.map(labToHex);
          };

          const selectDiv = (hexes:string[], k:number):string[] => {
            const labs = hexes.map(toLab);
            const lchs = labs.map(l=>toLch(l));
            const center = lchs.reduce((best,cur,i)=> cur.C<lchs[best].C?i:best,0);
            const leftLabs = labs.slice(0, center+1); // include center at end
            const rightLabs = labs.slice(center).reverse(); // include center at start mirrored
            const half = Math.floor(k/2);
            const hasCenter = k%2===1;
            const leftS = sampleEquidistant(leftLabs, hasCenter?half:half, 0.08);
            const rightS = sampleEquidistant(rightLabs, half, 0.08).reverse();
            const assembled: {L:number;a:number;b:number}[] = [];
            if (hasCenter) assembled.push(labs[center]);
            // Interleave symmetrically
            for(let i=0;i<half;i++){
              if(leftS[i]) assembled.unshift(leftS[i]);
              if(rightS[i]) assembled.push(rightS[i]);
            }
            // If even, remove duplicate center
            const resultLabs = assembled.slice(0,k);
            return resultLabs.map(labToHex);
          };

          const type = inferredType;
          let selectedColors: string[];
          if (type==='seq') selectedColors = selectSeq(palette, n);
          else if (type==='div') selectedColors = selectDiv(palette, n);
          else selectedColors = selectQual(palette, n);

          const hasArrayColors = (tr: any) => Array.isArray(tr?.marker?.color) || Array.isArray(tr?.marker?.colors);
          traces.forEach((trace: any, index: number) => {
            const color = selectedColors[index % selectedColors.length];
            if (trace.type === 'bar') {
              if (!trace.marker) trace.marker = {};
              if (singleBarPointColors) {
                // Manage per-point colors for single bar trace with multiple categories
                (trace as any)._lumiTheme = (trace as any)._lumiTheme || {};
                (trace as any)._lumiTheme.managedPerPoint = true;
                trace.marker.color = [...singleBarPointColors];
              } else if (!hasArrayColors(trace)) {
                trace.marker.color = color;
              }
              // Removed automatic palette opacity application - opacity should only be set explicitly
            } else if (trace.type === 'box' || trace.type === 'violin') {
              // Apply palette to box/violin traces (outline + interior)
              if (!trace.line) trace.line = {};
              trace.line.color = color;
              trace.fillcolor = color + '40';
              if (!trace.marker) trace.marker = {};
              if (trace.marker && trace.marker.color === undefined && !hasArrayColors(trace)) {
                trace.marker.color = color;
              }
            } else if (trace.type === 'scatter' || !trace.type) {
              if (trace.mode && trace.mode.includes('lines')) {
                if (!trace.line) trace.line = {};
                trace.line.color = color;
              }
              if (trace.mode && trace.mode.includes('markers')) {
                if (!trace.marker) trace.marker = {};
                if (!hasArrayColors(trace)) {
                  trace.marker.color = color;
                }
                // Removed automatic palette opacity application - opacity should only be set explicitly
              }
              if (!trace.mode) {
                if (!trace.marker) trace.marker = {};
                if (!hasArrayColors(trace)) {
                  trace.marker.color = color;
                }
                // Removed automatic palette opacity application - opacity should only be set explicitly
              }
            }
          });

          // Apply colorway for subsequent traces (always overwrite with selected palette)
          plotlyJson.layout.colorway = selectedColors.slice();
        }
        break;
      case 'design.showlegend':
        if (!plotlyJson.layout) plotlyJson.layout = {};
        plotlyJson.layout.showlegend = value;
        return true;
      default:
        setNestedProperty(plotlyJson.layout, path, value);
    }
    
    return true;
  };

  // General layout updates
  const applyLayoutUpdate = (plotlyJson: any, path: string, value: any): boolean => {
    if (!plotlyJson.layout) plotlyJson.layout = {};
    
    // Intercept barmode updates and route through helper function
    // This ensures offsetgroup/alignmentgroup are properly handled
    if (path === 'barmode') {
      const barTraces = (plotlyJson.data || []).filter((t: any) => t.type === 'bar');
      if (barTraces.length > 0) {
        // Map Plotly barmode values to helper function values
        let grouping: BarGrouping;
        if (value === 'stack' && plotlyJson.layout.barnorm === 'percent') {
          grouping = 'percent100';
        } else if (value === 'stack') {
          grouping = 'stacked';
        } else {
          grouping = 'grouped';
        }
        // Use the helper function to ensure proper handling
        return applyBarUpdate(plotlyJson, 'grouping', grouping);
      }
    }
    
    if (path === 'title') {
      // Support both string (legacy) and object (with subtitle) formats
      if (typeof value === 'string') {
      plotlyJson.layout.title = { text: value };
      } else if (typeof value === 'object' && value !== null) {
        // Preserve existing title properties while updating
        plotlyJson.layout.title = {
          ...(plotlyJson.layout.title || {}),
          ...value
        };
      }
    } else if (path === 'barnorm' && (value === null || value === undefined || value === '')) {
      // Handle clearing barnorm property
      if (plotlyJson.layout.barnorm !== undefined) {
        delete plotlyJson.layout.barnorm;
      }
    } else {
      setNestedProperty(plotlyJson.layout, path, value);
    }
    return true;
  };

  // Apply theme presets
  const applyTheme = (plotlyJson: any, theme: string) => {
    switch (theme) {
      case 'light':
        plotlyJson.layout.paper_bgcolor = '#ffffff';
        plotlyJson.layout.plot_bgcolor = '#ffffff';
        if (!plotlyJson.layout.font) plotlyJson.layout.font = {};
        plotlyJson.layout.font.color = '#333333';
        break;
      case 'dark':
        plotlyJson.layout.paper_bgcolor = '#1a1a1a';
        plotlyJson.layout.plot_bgcolor = '#2a2a2a';
        if (!plotlyJson.layout.font) plotlyJson.layout.font = {};
        plotlyJson.layout.font.color = '#ffffff';
        break;
      case 'nature':
        plotlyJson.layout.paper_bgcolor = '#ffffff';
        plotlyJson.layout.plot_bgcolor = '#ffffff';
        plotlyJson.layout.font = { 
          family: ((typeof window !== 'undefined' && (window as any).__lumiUiSansStack) || 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'),
          size: 10,
          color: '#000000'
        };
        break;
      case 'science':
        plotlyJson.layout.paper_bgcolor = '#ffffff';
        plotlyJson.layout.plot_bgcolor = '#ffffff';
        plotlyJson.layout.font = { 
          family: ((typeof window !== 'undefined' && (window as any).__lumiUiSansStack) || 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'),
          size: 11,
          color: '#000000'
        };
        break;
    }
  };

  // Helper to set nested properties
  const setNestedProperty = (obj: any, path: string, value: any) => {
    const parts = path.split('.');
    let current = obj;
    
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) {
        current[parts[i]] = {};
      }
      current = current[parts[i]];
    }
    
    // Simply set the value - 'transparent' string should work
    current[parts[parts.length - 1]] = value;
  };

  // Show editor even without a plot - users can explore controls

  // Determine trace types present to gate controls
  const hasBarTraces = useMemo(() => {
    try {
      const data:any[] = Array.isArray((currentPlot as any)?.plotlyJson?.data) ? ((currentPlot as any).plotlyJson.data as any[]) : [];
      return data.some((t: any) => t.type === 'bar');
    } catch { return false; }
  }, [(currentPlot as any)?.plotlyJson?.data]);

  const hasPerPointColors = useMemo(() => {
    try {
      const data:any[] = Array.isArray((currentPlot as any)?.plotlyJson?.data) ? ((currentPlot as any).plotlyJson.data as any[]) : [];
      const caps = unionCapabilities(data);
      return !!caps.hasPerPointColors;
    } catch { return false; }
  }, [(currentPlot as any)?.plotlyJson?.data]);

  // Reset to baseline handler: clears drafts, resets histories, restores baseline figure
  const handleResetToBaseline = useCallback(async () => {
    if (!currentPlot?.id) return;
    
    const plotId = currentPlot.id;
    const projectId = routeProjectId || (currentPlot as any).projectId;
    
    if (!projectId) {
      toast({
        title: 'Reset failed',
        description: 'Project ID not found.',
        variant: 'destructive',
      });
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm('Reset unsaved edits? This cannot be undone.');
    if (!confirmed) return;

    try {
      // Set resetting guard to prevent markUserEdited from re-marking dirty
      isResettingRef.current = true;
      (window as any).__lumiplotIsResetting = true;

      // Step 1: Clear local draft records (localStorage + IndexedDB)
      try {
        clearDraft(projectId, plotId);
        // Also clear IndexedDB draft
        try {
          const authEl: any = document.querySelector('meta[name="lumiplot-user-id"]');
          const userId = authEl?.getAttribute('content') || (window as any).__lumiUserId || null;
          const tenantId = (window as any).__lumiTenantId || null;
          const nsKey = makeDraftKey(tenantId, userId, projectId, plotId);
          await idbDeleteDraft(nsKey);
        } catch {}
      } catch (err) {
        console.warn('Failed to clear draft:', err);
      }

      // Step 2: Clear active session markers so RecoveryDialog cannot reapply changes
      try {
        clearActiveSessionMarkers(projectId, plotId);
      } catch (err) {
        console.warn('Failed to clear session markers:', err);
      }

      // Step 3: Reset store state (restores baseline figure, clears dirty flags, resets store editHistory)
      const resetResult = resetPlotToBaseline(plotId);
      if (!resetResult.ok) {
        throw new Error(resetResult.error || 'Failed to reset plot to baseline');
      }

      // Step 4: Reset hook history (plotlyJson history)
      // Read baseline figure fresh from store after reset succeeds
      const storeBaselineFigure = usePlotsStore.getState().baselineFigureById?.[plotId];
      if (storeBaselineFigure) {
        resetHistoryTo(storeBaselineFigure, 'Reset to saved');
      } else {
        console.warn('Baseline figure not found in store after reset, skipping history reset');
      }

      toast({
        title: 'Reset successful',
        description: 'Plot restored to last saved state.',
      });
    } catch (error) {
      console.error('Reset failed:', error);
      toast({
        title: 'Reset failed',
        description: error instanceof Error ? error.message : 'Failed to reset plot.',
        variant: 'destructive',
      });
    } finally {
      // Clear resetting guard after a short delay to allow state updates to complete
      setTimeout(() => {
        isResettingRef.current = false;
        (window as any).__lumiplotIsResetting = false;
      }, 100);
    }
  }, [currentPlot, routeProjectId, resetPlotToBaseline, resetHistoryTo, baselineFigure, toast]);

  return (
    <div className="h-full flex flex-col bg-card border-l border-border overflow-hidden">
      {/* Editor Header */}
      <div className="border-b border-border">
        <div className="flex flex-col gap-0.5 px-3 pt-3 pb-2 h-12">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Plot Editor</h2>
            
            <div className="flex items-center gap-2">
              {/* Undo/Redo/Reset */}
              <TooltipProvider>
                <div className="flex items-center gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={undo}
                        disabled={!canUndo() || !currentPlot || isProcessing}
                        className="h-8 w-8 p-0"
                      >
                        <Undo className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Undo</p>
                    </TooltipContent>
                  </Tooltip>
                  
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={redo}
                        disabled={!canRedo() || !currentPlot || isProcessing}
                        className="h-8 w-8 p-0"
                      >
                        <Redo className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Redo</p>
                    </TooltipContent>
                  </Tooltip>
                  
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResetToBaseline}
                        disabled={!canReset || !currentPlot || isProcessing}
                        className="h-8 w-8 p-0"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Reset to last saved</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
              {/* Generate button removed per gating UX: chat is the only entry point */}
              
              {onClose && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Subheader line below header to avoid changing header height */}
      {/* Removed states/ready subheader for cleaner typography */}
      
      <div className="flex-1 overflow-hidden flex flex-col">
        {!currentPlot ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-4 max-w-sm">
              <h3 className="text-lg font-medium">No plot created yet</h3>
              <p className="text-sm text-muted-foreground">
                Upload data and create a plot to enable these controls.
              </p>
            </div>
          </div>
        ) : (
          <PlotEditorRoot
            plotlyJson={currentPlot.plotlyJson}
            onUpdate={handleUpdate}
            dataColumns={dataColumns}
          >
            <PlotEditorTabs activeTab={activeTab} onTabChange={handleTabChange} />
          </PlotEditorRoot>
        )}
      </div>
      <OutOfCreditsDialog open={showOutOfCredits.open} onClose={() => setShowOutOfCredits({ open: false })} resetAt={showOutOfCredits.resetAt} />
      <BillingModal open={billingOpen} onOpenChange={setBillingOpen} />
    </div>
  );
}

